/**
 * Project assistant: the user asks in words ("ajoute un circuit pour la borne de recharge",
 * "passe le circuit C en 20 A", "génère le multifilaire"); the model (server side) or the local
 * demo parser answers with a reply and a list of *actions*. Actions are a small, closed
 * vocabulary turned into ops here, on the client, so the model never writes raw project data;
 * the user previews the result and applies it as one undo step.
 */
import { z } from 'zod';
import {
  applyTransaction,
  sheetsInOrder,
  standardName,
  tr,
  type Element,
  type Id,
  type Locale,
  type Op,
  type Project,
  type PropValue,
  type Standard,
} from '@elec/domain';
import { analyse, controlInfo, RCD_DEVICES } from '@elec/analysis';
import {
  checkProject,
  DEDICATED,
  earthNeeds,
  MAX_RATING_A,
  pointRooms,
  ruleInfo,
  rulesOf,
  standardOf,
  type Finding,
} from '@elec/rules';
import { INSTALL_METHODS, projectCalcs } from '@elec/calc';
import { getSymbol, ROOM_TYPES } from '@elec/symbols';
import {
  appendCircuitOps,
  appendGroupOps,
  appendPointsOps,
  deleteCircuitOps,
  generateBoardOps,
  moveCircuitOps,
  setPointsOps,
  generateMultiLineOps,
  type BoardSpec,
  numberTerminalsOps,
  numberWiresOps,
  renumberOps,
  deviceByTag,
  draftPlanOps,
  earthElectrodeOps,
  earthSocketsOps,
  regroupOps,
  splitCircuitOps,
  placeMissingOps,
  unplacedPoints,
} from '@elec/editor';
import { INSERT_TEMPLATES } from '@elec/samples';
import { correctFigures } from './figures.ts';
import {
  describedCircuitSchema,
  describedToSpec,
  parseCircuitText,
  type DescribedCircuit,
} from './circuits.ts';

export const TEMPLATE_IDS = ['ev-charger', 'pv', 'heat-pump', 'impulse-lighting', 'shutters'] as const;

const cableSchema = z.object({
  type: z.string(),
  cores: z.number().int(),
  sectionMm2: z.number().positive(),
  /** Cable length in metres (voltage drop, maximum protected length), when the user gave it. */
  lengthM: z.number().positive().max(1000).nullable().optional(),
  /** How it is laid (IEC 60364-5-52): A1 conduit in an insulated wall, B1 in conduit, C clipped direct. */
  installMethod: z.enum(INSTALL_METHODS).nullable().optional(),
});

const ROOM_TYPE_VALUES = ROOM_TYPES.map((r) => r.value) as [string, ...string[]];

export const assistActionSchema = z.discriminatedUnion('type', [
  z
    .object({ type: z.literal('add_circuits'), circuits: z.array(describedCircuitSchema) })
    .describe('Add new circuits to the single-line diagram'),
  z
    .object({
      type: z.literal('update_circuit'),
      code: z.string().describe('Circuit letter, e.g. "C"'),
      label: z.string().nullable(),
      ratingA: z.number().positive().nullable(),
      curve: z.enum(['B', 'C', 'D']).nullable(),
      cable: cableSchema.nullable(),
      breakingKa: z
        .number()
        .positive()
        .nullable()
        .optional()
        .describe('Breaking capacity of its device in kA, only as the user gave it (R-CALC-05)'),
    })
    .describe('Change an existing circuit; null fields are left unchanged'),
  z
    .object({
      type: z.literal('add_points'),
      code: z.string().describe('Circuit letter, e.g. "E"'),
      points: z.array(describedCircuitSchema.shape.points.element).min(1),
    })
    .describe('Add points (sockets, lights, appliances) at the end of an existing circuit'),
  z
    .object({
      type: z.literal('add_rcd_group'),
      sensitivityMa: z.number().positive().describe('30 for wet locations; 300 for the main RCD'),
      ratingA: z.number().positive().nullable(),
      rcdType: z.enum(['A', 'F', 'B']).nullable(),
      circuits: z.array(describedCircuitSchema),
      move: z
        .array(z.string())
        .nullable()
        .optional()
        .describe(
          'Letters of existing circuits to move under this new RCD (they keep their letter and points)',
        ),
    })
    .describe(
      'Add an RCD with its circuits to the board. On an empty single-line diagram, an RCD of 100 mA or more becomes the main RCD of a new board',
    ),
  z
    .object({
      type: z.literal('delete_circuit'),
      code: z.string().describe('Circuit letter, e.g. "G"'),
    })
    .describe('Remove a circuit the user named: its device, points and cable; the others close up'),
  z
    .object({
      type: z.literal('move_circuit'),
      code: z.string().describe('Circuit letter, e.g. "C"'),
      rcd: z.string().nullable().describe('Tag of the RCD to move it under, e.g. "Q7", from the context'),
      sensitivityMa: z
        .number()
        .positive()
        .nullable()
        .describe('Or the sensitivity of that RCD (30, 300) when the user named no tag'),
    })
    .describe('Move an existing circuit under another RCD, keeping its letter, points and cable'),
  z
    .object({
      type: z.literal('remove_points'),
      code: z.string().describe('Circuit letter'),
      kind: z.enum(['socket', 'light', 'appliance', 'switch']),
      count: z.number().int().min(1),
    })
    .describe('Remove points of a kind from the end of an existing circuit'),
  z
    .object({
      type: z.literal('set_main_switch'),
      ratingA: z.number().positive().nullable(),
      poles: z.number().int().min(1).max(4).nullable(),
    })
    .describe(
      'Change the rating or poles of the main switch (null fields unchanged); on an empty diagram, draw it',
    ),
  z
    .object({ type: z.literal('renumber') })
    .describe('Renumber circuits A, B, C… and their points in drawing order'),
  z
    .object({ type: z.literal('generate_multiline') })
    .describe('Generate or update the multi-line diagram of the board'),
  z.object({ type: z.literal('number_wires') }).describe('Number the wires of the control sheets'),
  z.object({ type: z.literal('number_terminals') }).describe('Number terminals 1…n in each terminal strip'),
  z
    .object({ type: z.literal('insert_template'), templateId: z.enum(TEMPLATE_IDS) })
    .describe('Insert a ready-made piece'),
  z
    .object({
      type: z.literal('draft_position_plan'),
      rooms: z.array(
        z.object({
          name: z
            .string()
            .describe("Room name in the user's language, as the user or a circuit label names it"),
          roomType: z.enum(ROOM_TYPE_VALUES),
          circuits: z.array(z.string()).describe('Letters of the circuits whose points are all in this room'),
          points: z
            .array(z.string())
            .describe('Codes of single points in this room (e.g. "C3"), for circuits spread over rooms'),
        }),
      ),
    })
    .describe(
      'When the project has no position plan: add one, with a box per room holding its points (codes as in the diagram). Points in no room are put aside for the user to place',
    ),
  z
    .object({
      type: z.literal('place_missing_points'),
      points: z
        .array(z.string())
        .nullable()
        .describe('Codes to add, e.g. ["A4"]; null for all the missing ones'),
    })
    .describe(
      'Add the diagram points missing from the existing position plan: next to a point of the same circuit, or put aside, for the user to move to their place',
    ),
  z
    .object({
      type: z.literal('update_rcd'),
      tag: z.string().describe('Tag of the RCD (or RCBO) from the context, e.g. "Q2"'),
      sensitivityMa: z.number().positive().nullable(),
      rcdType: z.enum(['A', 'F', 'B']).nullable(),
      ratingA: z.number().positive().nullable(),
    })
    .describe('Change an RCD: sensitivity, type or rating; null fields unchanged'),
  z
    .object({
      type: z.literal('split_circuit'),
      code: z.string().describe('Letter of the circuit to split'),
      kind: z.enum(['socket', 'light', 'appliance', 'switch']).describe('Kind of the points that move'),
      symbolId: z.string().nullable().describe('For an appliance: its symbol id, to move that one'),
      count: z.number().int().min(1).nullable().describe('How many of them move (the last ones); null: all'),
      label: z.string().describe('Label of the new circuit'),
      ratingA: z
        .number()
        .positive()
        .nullable()
        .describe('Rating of the new circuit; null: the same as the old one'),
      rcdMa: z
        .number()
        .positive()
        .nullable()
        .optional()
        .describe(
          '30 to put the new circuit under the 30 mA RCD (a washing machine, a dishwasher…); null: the same RCD',
        ),
    })
    .describe(
      'Move points of a circuit to a new circuit beside it, under the same RCD: more than 8 points, or an appliance that needs its own circuit',
    ),
  z
    .object({
      type: z.literal('earth_sockets'),
      codes: z.array(z.string()).nullable().describe('Letters of the circuits; null: every circuit'),
    })
    .describe('Replace sockets without earth contact by earthed sockets, on the diagram and the plan'),
  z
    .object({
      type: z.literal('set_earth_electrode'),
      resistanceOhm: z
        .number()
        .positive()
        .nullable()
        .describe('Measured earth resistance in Ω that the user gave; null when they gave none'),
    })
    .describe(
      'Draw the earth electrode on the single-line diagram when there is none, and/or set its measured resistance',
    ),
]);
export type AssistAction = z.infer<typeof assistActionSchema>;

export const assistResponseSchema = z.object({
  reply: z.string().describe('Short answer to the user, in their language'),
  actions: z.array(assistActionSchema),
});
export type AssistResponse = z.infer<typeof assistResponseSchema>;

export interface AssistMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Sent by a finding's "Fix with the assistant": the rule and what it points at (not sent to the model). */
  fix?: FixTarget;
}

/** The finding a request is meant to fix. */
export interface FixTarget {
  ruleId: string;
  /** Elements the finding points at (protective devices, points). */
  targets: Id[];
}

/**
 * What fixing a finding may change on the circuits it points at, beyond what the request's
 * words allow: the voltage drop is only fixed by a bigger section, which is rewiring (shown as
 * such in the proposal). Other rules need no exception.
 */
const FIX_ALLOWS: Record<string, Array<'cable' | 'rating'>> = {
  'R-CIR-02': ['rating', 'cable'],
  'R-CIR-08': ['rating', 'cable'],
  'FR-CIR-03': ['rating', 'cable'],
  'R-CALC-01': ['rating', 'cable'],
  'R-CALC-02': ['rating', 'cable'],
  'R-CALC-03': ['cable'],
  'R-CALC-04': ['cable'],
  'R-TEST-03': ['cable'],
};

/** Circuit letter → what the fix may change on it. */
function fixScope(
  analysis: ReturnType<typeof analyse>,
  fix?: FixTarget,
): Map<string, Set<'cable' | 'rating'>> {
  const out = new Map<string, Set<'cable' | 'rating'>>();
  const allows = fix && FIX_ALLOWS[fix.ruleId];
  if (!allows) return out;
  const t = new Set(fix.targets);
  for (const c of analysis.circuits)
    if (c.code && (t.has(c.device.id) || c.points.some((x) => t.has(x.element.id))))
      out.set(c.code.toUpperCase(), new Set(allows));
  return out;
}

/** Compact, text description of the project sent to the model (no personal data). */
/** Above this, the per-circuit detail lines are left out so the rules and findings still fit. */
const CONTEXT_BUDGET = 17000;

const n1 = (v: number) => String(Math.round(v * 10) / 10);

/**
 * What the model knows of the project: circuits with their rooms, loads and calculations, the
 * RCDs and main switch, and the findings the user sees (with why and fix in the user's
 * language, so the model can explain them in the user's terms), then the rules it is checked
 * against. `locale`: the interface language (the project's by default).
 */
export function projectContext(project: Project, locale?: Locale): string {
  const L = locale ?? project.meta.locale;
  const a = analyse(project);
  const calcs = projectCalcs(project, a);
  const rooms = pointRooms(project);
  const lines = [
    `Locale: ${project.meta.locale}. User's language: ${L}. Standard: ${standardName(project.meta.standard, 'en')}. Supply: ${project.meta.supplyVoltage}, earthing ${project.meta.earthingSystem}.`,
    'Sheets:',
  ];
  for (const s of sheetsInOrder(project))
    lines.push(`- ${s.name} (${s.kind}${s.source ? ', generated' : ''})`);
  lines.push(
    'Circuits (detail line: rooms from the position plan, connected loads, cable length and method, design current Ib, cable capacity Iz, voltage drop ΔU):',
  );
  for (const c of a.circuits) {
    lines.push(
      `- ${c.code || '?'} "${c.circuit?.label ?? ''}" ${c.device.tag} ${c.device.props.curve ?? ''}${c.ratingA ?? '?'} A, ` +
        `${c.cable.sectionMm2 ? `${c.cable.type} ${c.cable.cores}G${c.cable.sectionMm2}` : 'no cable'}, ` +
        `${c.points.length} points${pointSummary(c.points)}${codeList(c.points)}, RCDs: ${c.rcds.map((r) => `${r.tag} ${r.props.sensitivityMa} mA ${r.props.rcdType ?? ''}`).join(' > ') || 'none'}`,
    );
    const parts: string[] = [];
    const inRooms = [
      ...new Set(
        c.points
          .map((p) => rooms.get(p.element.tag))
          .filter((r): r is { type: string; name: string } => !!r)
          .map((r) => {
            const type = ROOM_TYPES.find((t) => t.value === r.type)?.label.en ?? r.type;
            return r.name && r.name.toLowerCase() !== type.toLowerCase() ? `${r.name} (${type})` : type;
          }),
      ),
    ];
    if (inRooms.length) parts.push(`rooms: ${inRooms.join(', ')}`);
    const k = calcs.get(c.device.id);
    if (k) {
      if (k.loadKw !== undefined)
        parts.push(`load ${n1(k.loadKw)} kW${k.threePhase ? ' (three-phase)' : ''}`);
      if (k.lengthM) parts.push(`length ${k.lengthM} m, method ${k.method}`);
      else parts.push('length unknown');
      if (k.ib !== undefined)
        parts.push(
          `Ib ${n1(k.ib)} A (${k.ibSource === 'loads' ? 'from the loads' : 'taken as the rating: loads unknown'})`,
        );
      if (k.iz !== undefined) parts.push(`Iz ${n1(k.iz)} A`);
      if (k.dropPct !== undefined) parts.push(`ΔU ${n1(k.dropPct)} % (max ${k.dropLimitPct} %)`);
      if (k.lMaxM !== undefined && k.lengthM) parts.push(`max protected length ${Math.floor(k.lMaxM)} m`);
    }
    if (parts.length) lines.push(`    ${parts.join('; ')}`);
  }
  // RCDs and the main switch, so the model can name them (move_circuit, set_main_switch).
  const sl = sheetsInOrder(project).find((s) => s.kind === 'single-line');
  const onSl = Object.values(project.elements).filter((e) => e.sheetId === sl?.id);
  const rcdList = onSl
    .filter((e) => RCD_DEVICES.has(e.symbolId) && e.symbolId !== 'iec.rcbo')
    .map((e) => `${e.tag} ${e.props.sensitivityMa ?? '?'} mA ${e.props.rcdType ?? ''}`.trim());
  if (rcdList.length) lines.push(`RCDs: ${rcdList.join(', ')}`);
  const mainSwitch = onSl.find((e) => e.symbolId === 'iec.switch-disconnector');
  lines.push(
    mainSwitch
      ? `Main switch: ${mainSwitch.tag} ${mainSwitch.props.ratingA ?? '?'} A, ${mainSwitch.props.poles ?? 2} poles`
      : 'Main switch: none drawn',
  );
  const electrode = Object.values(project.elements).find((e) => e.symbolId === 'iec.earth-electrode');
  const earth = earthNeeds(project, a);
  const limit = earth.required
    ? ` (required, max. ${earth.maxOhm} Ω)`
    : ' (not required with this earthing system)';
  lines.push(
    electrode
      ? `Earth electrode: ${electrode.tag || 'PE'}, ${electrode.props.resistanceOhm === undefined || electrode.props.resistanceOhm === '' ? 'resistance not stated' : `${electrode.props.resistanceOhm} Ω`}${limit}`
      : `Earth electrode: none drawn${limit}`,
  );
  const relays = [...controlInfo(project).relays.values()];
  if (relays.length)
    lines.push(
      `Relays: ${relays.map((r) => `${r.tag} (${r.heads.length} coil, ${r.contacts.length} contacts)`).join(', ')}`,
    );
  lines.push(...findingLines(project, L));
  // The rules of the standard are no longer here: the server puts them in the system prompt,
  // the same for every project of that standard, so the model server reuses them (prefix cache).
  // Last, nearest the conversation: Qwen answered Dutch requests in French when the language
  // was only said at the top (the project's labels and the rules above are not in it).
  lines.push(`Write "reply" in ${LANGUAGE_NAME[L]} (${OPENER[L]}).`);
  const text = lines.join('\n');
  if (text.length <= CONTEXT_BUDGET) return text;
  // Large project: drop the detail lines first.
  return lines.filter((l) => !l.startsWith('    ')).join('\n');
}

/** " [A1–A6]": the points' codes, runs of numbers shortened (for the position plan actions). */
function codeList(points: Array<{ element: { tag: string } }>): string {
  const tags = points.map((p) => p.element.tag).filter(Boolean);
  if (!tags.length) return '';
  const out: string[] = [];
  let start = 0;
  const num = (t: string) => Number(/(\d+)$/.exec(t)?.[1] ?? NaN);
  const prefix = (t: string) => t.replace(/\d+$/, '');
  for (let i = 1; i <= tags.length; i++) {
    const run =
      i < tags.length && prefix(tags[i]!) === prefix(tags[i - 1]!) && num(tags[i]!) === num(tags[i - 1]!) + 1;
    if (run) continue;
    out.push(i - 1 > start ? `${tags[start]}–${tags[i - 1]}` : tags[start]!);
    start = i;
  }
  return ` [${out.join(', ')}]`;
}

/** " (6 sockets, 1 oven)": what a circuit feeds, by kind and appliance. */
function pointSummary(points: Array<{ element: { symbolId: string }; kind: string }>): string {
  if (!points.length) return '';
  const counts = new Map<string, number>();
  for (const p of points) {
    const what =
      p.kind === 'appliance' || p.kind === 'other'
        ? (getSymbol(p.element.symbolId)?.name.en.toLowerCase() ?? 'appliance')
        : `${p.kind}s`;
    counts.set(what, (counts.get(what) ?? 0) + 1);
  }
  return ` (${[...counts].map(([w, n]) => `${n} ${w}`).join(', ')})`;
}

/** The findings of the check panel, each with why and fix in the user's language. */
function findingLines(project: Project, L: Locale): string[] {
  const findings = checkProject(project).filter((f) => f.severity !== 'info');
  if (!findings.length) return ['Findings: none (errors and warnings).'];
  const std = standardOf(project);
  const out = [
    `Findings the user sees in the check panel (${findings.length}); "why" and "fix" are in the user's language:`,
  ];
  for (const f of findings.slice(0, 20)) {
    const info = ruleInfo(f.ruleId, std);
    out.push(
      `- ${f.severity} ${f.ruleId}: ${f.message[L] ?? f.message.fr}` +
        (info ? `. Why: ${info.why[L] ?? info.why.fr} Fix: ${info.fix[L] ?? info.fix.fr}` : ''),
    );
  }
  if (findings.length > 20) out.push(`- … ${findings.length - 20} more`);
  return out;
}

/**
 * The design rules the editor checks for the project's standard (from the rule pack, so the
 * model and the checker agree), plus the rating-per-section table behind R-CIR-02.
 */
export function designRules(project: Project): string[] {
  return designRulesFor(standardOf(project));
}

/** The design rules of a standard, as the system prompt gives them (see `designRules`). */
export function designRulesFor(standard: Standard): string[] {
  const rules = rulesOf(standard).filter((r) => /^(R|FR|NL)-(RCD|CIR)-/.test(r.id));
  return [
    'Rules the editor checks on every proposal:',
    ...rules.map((r) => `- ${r.id} ${r.title.en}: ${r.why.en}`),
    `- Max rating per copper section: ${Object.entries(MAX_RATING_A)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([mm2, a]) => `${mm2} mm² → ${a} A`)
      .join(', ')}.`,
  ];
}

const singleLine = (project: Project, sheetId?: Id) =>
  (sheetId && project.sheets[sheetId]?.kind === 'single-line' ? project.sheets[sheetId] : undefined) ??
  sheetsInOrder(project).find((s) => s.kind === 'single-line');

/** Ops adding described circuits; a circuit asking for 30 mA goes under a 30 mA RCD when there is one. */
export function addDescribedOps(
  project: Project,
  sheetId: Id,
  circuits: DescribedCircuit[],
  rcdId?: Id,
): Op[] {
  let p = project;
  const ops: Op[] = [];
  for (const d of circuits) {
    const s = describedToSpec(d);
    const rcds = Object.values(p.elements).filter(
      (e) => e.sheetId === sheetId && RCD_DEVICES.has(e.symbolId) && e.symbolId !== 'iec.rcbo',
    );
    // An RCBO protects itself: it goes on the main (≥ 100 mA) RCD's bus, not under a 30 mA one.
    const main = rcds
      .filter((r) => Number(r.props.sensitivityMa) >= 100)
      .sort((a, b) => Number(b.props.sensitivityMa) - Number(a.props.sensitivityMa))[0]?.id;
    const wanted = s.rcdMa
      ? rcds.find((r) => Number(r.props.sensitivityMa) === s.rcdMa)?.id
      : d.device === 'RCBO'
        ? main
        : undefined;
    const o = appendCircuitOps(p, sheetId, s, wanted ?? rcdId);
    ops.push(...o);
    p = applyTransaction(p, { label: '', ops: o }).project;
  }
  return ops;
}

export interface ActionResult {
  ops: Op[];
  /** One line per action, in the project's language (what will change). */
  summary: string[];
  /** For each summary line, the index of the action it comes from (to apply only some). */
  actionOf: number[];
  /** Actions that could not be applied, with the reason. */
  skipped: string[];
  /** Sheet to show afterwards. */
  sheetId?: Id;
}

/** The sheet's elements when it holds nothing but a supply chain (or nothing), else null. */
function supplyChainOnly(project: Project, sheetId: Id): Element[] | null {
  const on = Object.values(project.elements).filter((e) => e.sheetId === sheetId);
  return on.every((e) => SUPPLY_CHAIN.has(e.symbolId)) ? on : null;
}
// The earth electrode stands beside the chain: kept (with its value) when the board is drawn.
const SUPPLY_CHAIN = new Set(['iec.supply', 'iec.meter', 'iec.switch-disconnector', 'iec.earth-electrode']);

function clearSheetOps(project: Project, sheetId: Id): Op[] {
  return [
    ...Object.values(project.wires)
      .filter((w) => w.sheetId === sheetId)
      .map((w): Op => ({ op: 'remove', coll: 'wires', id: w.id })),
    ...Object.values(project.elements)
      .filter((e) => e.sheetId === sheetId && e.symbolId !== 'iec.earth-electrode')
      .map((e): Op => ({ op: 'remove', coll: 'elements', id: e.id })),
  ];
}

function mainSwitchProps(
  props: Record<string, PropValue>,
  a?: Extract<AssistAction, { type: 'set_main_switch' }>,
): Record<string, PropValue> {
  return {
    ...props,
    ratingA: a?.ratingA ?? props.ratingA ?? '',
    poles: a?.poles ?? props.poles ?? 2,
  };
}

const mainSwitchLine = (L: Locale, props: Record<string, PropValue>) =>
  tr(L, 'Interrupteur général', 'Main switch', 'Hoofdschakelaar') +
  ` ${props.ratingA === '' ? '?' : props.ratingA} A, ${props.poles}P`;

/** Turn actions into ops, each computed on the project as left by the previous ones. */
export function actionsToOps(
  project: Project,
  actions: AssistAction[],
  currentSheetId?: Id,
  /** Language of the summary and notes (the interface's); the project's own by default. */
  locale?: Locale,
): ActionResult {
  const L = locale ?? project.meta.locale;
  let p = project;
  const ops: Op[] = [];
  const summary: string[] = [];
  const skipped: string[] = [];
  let sheetId: Id | undefined;
  const actionOf: number[] = [];
  let current = 0;
  const push = (o: Op[], line: string, show?: Id) => {
    if (!o.length) return;
    ops.push(...o);
    p = applyTransaction(p, { label: '', ops: o }).project;
    summary.push(line);
    actionOf.push(current);
    if (show) sheetId = show;
  };
  const notFound = (code: string) =>
    tr(L, `Circuit ${code} introuvable`, `Circuit ${code} not found`, `Kring ${code} niet gevonden`);
  const consumed = new Set<AssistAction>();
  for (const [i, a] of actions.entries()) {
    current = i;
    const sl = singleLine(p, currentSheetId);
    switch (a.type) {
      case 'add_circuits': {
        if (!sl) {
          skipped.push(tr(L, 'Pas de schéma unifilaire', 'No single-line diagram', 'Geen eendraadschema'));
          break;
        }
        push(
          addDescribedOps(p, sl.id, a.circuits),
          tr(L, 'Ajouter : ', 'Add: ', 'Toevoegen: ') + a.circuits.map((c) => c.label || '?').join(', '),
          sl.id,
        );
        break;
      }
      case 'update_circuit': {
        const c = analyse(p).circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(
            tr(
              L,
              `Circuit ${a.code} introuvable`,
              `Circuit ${a.code} not found`,
              `Kring ${a.code} niet gevonden`,
            ),
          );
          break;
        }
        if (a.label === null && a.ratingA === null && a.curve === null && a.cable === null && !a.breakingKa) {
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} : rien à modifier (pour ajouter des points : add_points ; pour le déplacer : move_circuit)`,
              `Circuit ${c.code}: nothing to change (to add points use add_points; to move it use move_circuit)`,
              `Kring ${c.code}: niets te wijzigen (punten toevoegen: add_points; verplaatsen: move_circuit)`,
            ),
          );
          break;
        }
        const o: Op[] = [];
        const props = { ...c.device.props };
        if (a.ratingA !== null) props.ratingA = a.ratingA;
        if (a.curve !== null) props.curve = a.curve;
        if (a.breakingKa) props.breakingKa = a.breakingKa;
        if (a.ratingA !== null || a.curve !== null || a.breakingKa)
          o.push({ op: 'update', coll: 'elements', id: c.device.id, patch: { props } });
        if (c.circuit && (a.label !== null || a.cable !== null))
          o.push({
            op: 'update',
            coll: 'circuits',
            id: c.circuit.id,
            patch: {
              ...(a.label !== null ? { label: a.label } : {}),
              ...(a.cable !== null
                ? {
                    cable: {
                      ...c.circuit.cable,
                      type: a.cable.type,
                      cores: a.cable.cores,
                      sectionMm2: a.cable.sectionMm2,
                      ...(a.cable.lengthM ? { lengthM: a.cable.lengthM } : {}),
                      ...(a.cable.installMethod ? { installMethod: a.cable.installMethod } : {}),
                    },
                  }
                : {}),
            },
          });
        const what = [
          a.label,
          a.curve || a.ratingA ? `${a.curve ?? c.device.props.curve ?? ''}${a.ratingA ?? c.ratingA} A` : null,
          a.breakingKa ? `${a.breakingKa} kA` : null,
          a.cable
            ? `${a.cable.type} ${a.cable.cores}G${a.cable.sectionMm2}` +
              (a.cable.installMethod && a.cable.installMethod !== (c.circuit?.cable.installMethod || 'B1')
                ? tr(
                    L,
                    `, pose ${a.cable.installMethod}`,
                    `, method ${a.cable.installMethod}`,
                    `, methode ${a.cable.installMethod}`,
                  )
                : '') +
              (c.cable.sectionMm2 && a.cable.sectionMm2 !== c.cable.sectionMm2
                ? tr(L, ' (recâblage)', ' (rewiring)', ' (herbekabeling)')
                : '')
            : null,
        ]
          .filter(Boolean)
          .join(', ');
        push(
          o,
          `${tr(L, 'Circuit', 'Circuit', 'Kring')} ${c.code} → ${what}${calcDelta(p, o, c.device.id, L)}`,
          c.sheet.id,
        );
        break;
      }
      case 'add_points': {
        const c = analyse(p).circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(
            tr(
              L,
              `Circuit ${a.code} introuvable`,
              `Circuit ${a.code} not found`,
              `Kring ${a.code} niet gevonden`,
            ),
          );
          break;
        }
        const pts = describedToSpec({
          label: '',
          device: 'MCB',
          ratingA: null,
          curve: null,
          points: a.points,
          cable: null,
          rcdMa: null,
        }).points;
        push(
          appendPointsOps(p, c.device.id, pts),
          tr(
            L,
            `Circuit ${c.code} : ajouter ${pts.length} point(s)`,
            `Circuit ${c.code}: add ${pts.length} point(s)`,
            `Kring ${c.code}: ${pts.length} punt(en) toevoegen`,
          ),
          c.sheet.id,
        );
        break;
      }
      case 'add_rcd_group': {
        if (consumed.has(a)) break;
        if (!sl) {
          skipped.push(tr(L, 'Pas de schéma unifilaire', 'No single-line diagram', 'Geen eendraadschema'));
          break;
        }
        const rcdOf = (g: typeof a) => ({
          ratingA: g.ratingA ?? 40,
          sensitivityMa: g.sensitivityMa,
          rcdType: g.rcdType ?? 'A',
          poles: 2,
        });
        const specsOf = (g: typeof a) =>
          g.circuits.map((d) => {
            const { rcdMa: _, ...spec } = describedToSpec(d);
            return spec;
          });
        const line = (g: typeof a) =>
          tr(
            L,
            `Ajouter un différentiel ${g.sensitivityMa} mA`,
            `Add a ${g.sensitivityMa} mA RCD`,
            `Differentieel ${g.sensitivityMa} mA toevoegen`,
          ) +
          (g.circuits.length
            ? `${L === 'fr' ? ' : ' : ': '}${g.circuits.map((c) => c.label || '?').join(', ')}`
            : '');
        const chain = supplyChainOnly(p, sl.id);
        if (chain) {
          // Empty diagram (or only the supply, meter and main switch): every RCD group of this
          // answer goes into one generated board; the first group of 100 mA or more is the main
          // RCD (300 mA type A when there is none). A main switch already drawn, or asked for in
          // this answer, is drawn in the same chain.
          const groups = actions.filter(
            (x): x is Extract<AssistAction, { type: 'add_rcd_group' }> => x.type === 'add_rcd_group',
          );
          groups.forEach((g) => consumed.add(g));
          const sw = actions.find(
            (x): x is Extract<AssistAction, { type: 'set_main_switch' }> => x.type === 'set_main_switch',
          );
          if (sw) consumed.add(sw);
          const drawn = chain.find((e) => e.symbolId === 'iec.switch-disconnector');
          const mainSwitch = sw || drawn ? mainSwitchProps(drawn?.props ?? {}, sw) : undefined;
          const main = groups.find((g) => g.sensitivityMa >= 100);
          const board: BoardSpec = {
            ...(earthNeeds(p).required ? { earth: {} } : {}),
            ...(mainSwitch ? { main: mainSwitch } : {}),
            mainRcd: main ? rcdOf(main) : { ratingA: 40, sensitivityMa: 300, rcdType: 'A', poles: 2 },
            groups: [
              ...(main ? [{ circuits: specsOf(main) }] : []),
              ...groups.filter((g) => g !== main).map((g) => ({ rcd: rcdOf(g), circuits: specsOf(g) })),
            ],
          };
          // The drawn chain is redrawn with the board (same tags: removed first).
          const clear = clearSheetOps(p, sl.id);
          const cleared = applyTransaction(p, { label: '', ops: clear }).project;
          const lines = [...(sw && mainSwitch ? [mainSwitchLine(L, mainSwitch)] : []), ...groups.map(line)];
          push(
            [...clear, ...generateBoardOps(cleared, sl.id, board)],
            lines.join(L === 'fr' ? ' ; ' : '; '),
            sl.id,
          );
          break;
        }
        const origin = analyse(p)
          .circuits.filter((c) => c.sheet.id === sl.id)
          .map((c) => c.rcds.at(-1))
          .find(Boolean);
        if (!origin) {
          // Drawn circuits or devices but no RCD feeding circuits: a board would be drawn over them.
          skipped.push(
            tr(
              L,
              'Le schéma n’a pas de différentiel relié à des circuits : ajoutez le différentiel à la main',
              'The diagram has no RCD feeding circuits: add the RCD by hand',
              'Het schema heeft geen differentieel dat kringen voedt: voeg het differentieel met de hand toe',
            ),
          );
          break;
        }
        if (a.move?.length) {
          const an = analyse(p);
          const found = a.move.map((code) =>
            an.circuits.find((x) => x.code.toUpperCase() === code.toUpperCase()),
          );
          a.move.forEach((code, i) => {
            if (!found[i]) skipped.push(notFound(code));
          });
          const moving = found.filter((c): c is NonNullable<typeof c> => !!c);
          const r = regroupOps(
            p,
            sl.id,
            rcdOf(a),
            specsOf(a),
            moving.map((c) => c.device.id),
          );
          push(
            r.ops,
            line(a) +
              (moving.length
                ? tr(L, ', y déplacer ', ', moving ', ', met ') +
                  moving.map((c) => c.code).join(', ') +
                  tr(L, '', ' under it', ' eronder')
                : ''),
            sl.id,
          );
          break;
        }
        push(appendGroupOps(p, sl.id, rcdOf(a), specsOf(a)), line(a), sl.id);
        break;
      }
      case 'delete_circuit': {
        const c = analyse(p).circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(notFound(a.code));
          break;
        }
        push(
          deleteCircuitOps(p, c.device.id),
          tr(L, 'Supprimer le circuit', 'Delete circuit', 'Kring verwijderen') +
            ` ${c.code}${c.circuit?.label ? ` (${c.circuit.label})` : ''}`,
          c.sheet.id,
        );
        break;
      }
      case 'move_circuit': {
        const an = analyse(p);
        const c = an.circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(notFound(a.code));
          break;
        }
        const rcds = Object.values(p.elements).filter(
          (e) => e.sheetId === c.sheet.id && RCD_DEVICES.has(e.symbolId) && e.symbolId !== 'iec.rcbo',
        );
        const byTag = a.rcd ? rcds.find((r) => r.tag.toUpperCase() === a.rcd!.toUpperCase()) : undefined;
        const bySens = a.sensitivityMa
          ? rcds.filter((r) => Number(r.props.sensitivityMa) === a.sensitivityMa)
          : [];
        // The main RCD feeds its circuits from the main bus; another RCD of that sensitivity is ambiguous.
        const target = byTag ?? (bySens.length === 1 ? bySens[0] : undefined);
        if (!target) {
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} : différentiel ${a.rcd ?? `${a.sensitivityMa} mA`} introuvable ou ambigu`,
              `Circuit ${c.code}: RCD ${a.rcd ?? `${a.sensitivityMa} mA`} not found or ambiguous`,
              `Kring ${c.code}: differentieel ${a.rcd ?? `${a.sensitivityMa} mA`} niet gevonden of dubbelzinnig`,
            ),
          );
          break;
        }
        if (c.rcds[0]?.id === target.id) {
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} est déjà sous ${target.tag}`,
              `Circuit ${c.code} is already under ${target.tag}`,
              `Kring ${c.code} zit al onder ${target.tag}`,
            ),
          );
          break;
        }
        push(
          moveCircuitOps(p, c.device.id, target.id, an),
          tr(L, 'Déplacer le circuit', 'Move circuit', 'Kring verplaatsen') +
            ` ${c.code} → ${target.tag} ${target.props.sensitivityMa} mA`,
          c.sheet.id,
        );
        break;
      }
      case 'remove_points': {
        const c = analyse(p).circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(notFound(a.code));
          break;
        }
        const own = [...c.points]
          .filter((x) => x.element.sheetId === c.sheet.id)
          .sort((x, y) => x.element.y - y.element.y);
        const ofKind = own.filter((x) => x.kind === a.kind);
        if (ofKind.length < a.count) {
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} : seulement ${ofKind.length} point(s) de ce type`,
              `Circuit ${c.code}: only ${ofKind.length} point(s) of that kind`,
              `Kring ${c.code}: slechts ${ofKind.length} punt(en) van dat type`,
            ),
          );
          break;
        }
        const gone = new Set(ofKind.slice(-a.count).map((x) => x.element.id));
        const keptEls = own.filter((x) => !gone.has(x.element.id));
        const kept = keptEls.map((x) => ({ symbolId: x.element.symbolId, props: { ...x.element.props } }));
        push(
          setPointsOps(
            p,
            c.device.id,
            kept,
            undefined,
            keptEls.map((x) => x.element.id),
          ),
          tr(
            L,
            `Circuit ${c.code} : retirer ${a.count} point(s)`,
            `Circuit ${c.code}: remove ${a.count} point(s)`,
            `Kring ${c.code}: ${a.count} punt(en) verwijderen`,
          ),
          c.sheet.id,
        );
        break;
      }
      case 'set_main_switch': {
        if (consumed.has(a)) break;
        const m = Object.values(p.elements).find(
          (e) => e.sheetId === sl?.id && e.symbolId === 'iec.switch-disconnector',
        );
        // Empty diagram: an RCD group of this answer draws the main switch with the board;
        // alone, it is drawn after the supply and meter.
        if (!m && sl && actions.some((x) => x.type === 'add_rcd_group') && supplyChainOnly(p, sl.id)) break;
        if (
          !m &&
          sl &&
          !Object.values(p.elements).some((e) => e.sheetId === sl.id && e.symbolId !== 'iec.earth-electrode')
        ) {
          const props = mainSwitchProps({}, a);
          push(
            generateBoardOps(p, sl.id, {
              main: props,
              groups: [],
              ...(earthNeeds(p).required ? { earth: {} } : {}),
            }),
            mainSwitchLine(L, props),
            sl.id,
          );
          break;
        }
        if (!m) {
          skipped.push(
            tr(
              L,
              'Pas d’interrupteur général sur le schéma : ajoutez-le à la main',
              'No main switch on the diagram: add it by hand',
              'Geen hoofdschakelaar op het schema: voeg hem met de hand toe',
            ),
          );
          break;
        }
        const props = { ...m.props };
        if (a.ratingA !== null) props.ratingA = a.ratingA;
        if (a.poles !== null) props.poles = a.poles;
        push(
          a.ratingA === null && a.poles === null
            ? []
            : [{ op: 'update', coll: 'elements', id: m.id, patch: { props } }],
          tr(L, 'Interrupteur général', 'Main switch', 'Hoofdschakelaar') +
            ` ${m.tag} → ${props.ratingA ?? '?'} A, ${props.poles ?? 2}P`,
          m.sheetId,
        );
        break;
      }
      case 'renumber':
        push(renumberOps(p), tr(L, 'Renuméroter les circuits', 'Renumber circuits', 'Kringen hernummeren'));
        break;
      case 'generate_multiline': {
        if (!sl) {
          skipped.push(tr(L, 'Pas de schéma unifilaire', 'No single-line diagram', 'Geen eendraadschema'));
          break;
        }
        const r = generateMultiLineOps(p, sl.id);
        push(
          r.ops,
          tr(
            L,
            'Générer le schéma multifilaire',
            'Generate the multi-line diagram',
            'Meerdraadsschema maken',
          ),
          r.sheetId,
        );
        break;
      }
      case 'number_wires': {
        const o: Op[] = [];
        let start = 1;
        for (const s of sheetsInOrder(p).filter((x) => x.kind === 'control')) {
          const w = numberWiresOps(applyTransaction(p, { label: '', ops: o }).project, s.id, start);
          o.push(...w);
          start += 100;
        }
        push(o, tr(L, 'Numéroter les fils', 'Number wires', 'Draden nummeren'));
        break;
      }
      case 'number_terminals':
        push(numberTerminalsOps(p), tr(L, 'Numéroter les bornes', 'Number terminals', 'Klemmen nummeren'));
        break;
      case 'insert_template': {
        const t = INSERT_TEMPLATES.find((x) => x.id === a.templateId)!;
        const r = t.ops(p, sl?.id);
        if (!r.ops.length)
          skipped.push(
            tr(
              L,
              `${t.name.fr} : pas de schéma unifilaire`,
              `${t.name.en}: no single-line diagram`,
              `${t.name.nl}: geen eendraadschema`,
            ),
          );
        push(r.ops, tr(L, 'Insérer : ', 'Insert: ', 'Invoegen: ') + (t.name[L] ?? t.name.fr), r.sheetId);
        break;
      }
      case 'update_rcd': {
        const r = deviceByTag(p, a.tag);
        if (!r || !RCD_DEVICES.has(r.symbolId)) {
          skipped.push(
            tr(
              L,
              `Différentiel ${a.tag} introuvable`,
              `RCD ${a.tag} not found`,
              `Differentieel ${a.tag} niet gevonden`,
            ),
          );
          break;
        }
        const props = {
          ...r.props,
          ...(a.sensitivityMa !== null ? { sensitivityMa: a.sensitivityMa } : {}),
          ...(a.rcdType !== null ? { rcdType: a.rcdType } : {}),
          ...(a.ratingA !== null ? { ratingA: a.ratingA } : {}),
        };
        const changed = (['sensitivityMa', 'rcdType', 'ratingA'] as const).filter(
          (k) => props[k] !== r.props[k],
        );
        if (!changed.length) {
          skipped.push(
            tr(L, `${r.tag} : rien à modifier`, `${r.tag}: nothing to change`, `${r.tag}: niets te wijzigen`),
          );
          break;
        }
        push(
          [{ op: 'update', coll: 'elements', id: r.id, patch: { props } }],
          `${r.tag} → ${props.sensitivityMa ?? '?'} mA type ${props.rcdType ?? '?'}, ${props.ratingA ?? '?'} A`,
          r.sheetId,
        );
        break;
      }
      case 'split_circuit': {
        const an = analyse(p);
        const c = an.circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
        if (!c) {
          skipped.push(notFound(a.code));
          break;
        }
        const ofKind = [...c.points]
          .filter(
            (x) =>
              x.element.sheetId === c.sheet.id &&
              x.kind === a.kind &&
              (!a.symbolId || x.element.symbolId === a.symbolId),
          )
          .sort((x, y) => x.element.y - y.element.y);
        const moving = a.count ? ofKind.slice(-a.count) : ofKind;
        const rcd30 = a.rcdMa
          ? Object.values(p.elements).find(
              (e) =>
                e.sheetId === c.sheet.id &&
                RCD_DEVICES.has(e.symbolId) &&
                e.symbolId !== 'iec.rcbo' &&
                Number(e.props.sensitivityMa) === a.rcdMa,
            )?.id
          : undefined;
        const r = moving.length
          ? splitCircuitOps(
              p,
              c.device.id,
              moving.map((x) => x.element.id),
              {
                label: a.label,
                ...(a.ratingA ? { deviceProps: { ratingA: a.ratingA } } : {}),
                ...(rcd30 ? { rcdId: rcd30 } : {}),
              },
            )
          : { ops: [] as Op[] };
        if (!r.ops.length && moving.length > 0 && moving.length === c.points.length) {
          // The circuit already feeds only this: a pasted line that repeats it is not a split.
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} : il n’alimente déjà que ces points, c’est déjà leur propre circuit (pour un nouvel appareil, ajoutez un nouveau circuit ; sinon laissez ${c.code} tel quel)`,
              `Circuit ${c.code}: it already feeds only these points, so it is already their own circuit (for a new appliance, add a new circuit; otherwise leave ${c.code} as it is)`,
              `Kring ${c.code}: voedt al alleen deze punten, het is dus al hun eigen kring (voor een nieuw toestel een nieuwe kring toevoegen; anders ${c.code} laten zoals hij is)`,
            ),
          );
          break;
        }
        if (!r.ops.length) {
          skipped.push(
            tr(
              L,
              `Circuit ${c.code} : impossible de séparer ces points (il en faut au moins un de chaque côté)`,
              `Circuit ${c.code}: cannot split these points (at least one must stay on each side)`,
              `Kring ${c.code}: deze punten kunnen niet worden afgesplitst (aan elke kant minstens één)`,
            ),
          );
          break;
        }
        push(
          r.ops,
          tr(
            L,
            `Séparer ${moving.length} point(s) du circuit ${c.code} vers un nouveau circuit « ${a.label} »`,
            `Move ${moving.length} point(s) of circuit ${c.code} to a new circuit “${a.label}”`,
            `${moving.length} punt(en) van kring ${c.code} naar een nieuwe kring “${a.label}”`,
          ),
          c.sheet.id,
        );
        break;
      }
      case 'earth_sockets': {
        const o = earthSocketsOps(p, a.codes ?? undefined);
        if (!o.length) {
          skipped.push(
            tr(L, 'Aucune prise sans terre', 'No socket without earth', 'Geen stopcontact zonder aarding'),
          );
          break;
        }
        const n = o.filter(
          (x) => x.op === 'update' && p.sheets[p.elements[x.id]?.sheetId ?? '']?.kind === 'single-line',
        ).length;
        push(
          o,
          tr(
            L,
            `Remplacer ${n} prise(s) sans terre par des prises avec terre`,
            `Replace ${n} socket(s) without earth by earthed sockets`,
            `${n} stopcontact(en) zonder aarding vervangen door geaarde`,
          ),
          sl?.id,
        );
        break;
      }
      case 'set_earth_electrode': {
        const r = earthElectrodeOps(p, sl?.id, a.resistanceOhm ?? undefined);
        // Nothing to change and nothing asked (the board drew the electrode): fine as it is.
        if (!r.ops.length && r.id && a.resistanceOhm == null) break;
        if (!r.ops.length) {
          skipped.push(
            !sl && !r.id
              ? tr(L, 'Pas de schéma unifilaire', 'No single-line diagram', 'Geen eendraadschema')
              : tr(
                  L,
                  'Prise de terre déjà au schéma, sans nouvelle valeur',
                  'Earth electrode already on the diagram, with no new value',
                  'Aardverbinding staat al op het schema, zonder nieuwe waarde',
                ),
          );
          break;
        }
        const { maxOhm } = earthNeeds(p, analyse(p));
        const v = a.resistanceOhm;
        const value =
          v == null
            ? tr(L, 'résistance à mesurer', 'resistance to measure', 'weerstand te meten')
            : `${v} Ω${v > maxOhm ? ` (max. ${maxOhm} Ω)` : ''}`;
        push(
          r.ops,
          (r.added
            ? tr(L, 'Ajouter la prise de terre', 'Add the earth electrode', 'Aardverbinding toevoegen')
            : tr(L, 'Prise de terre', 'Earth electrode', 'Aardverbinding')) +
            `${L === 'fr' ? ' : ' : ': '}${value}`,
          project.elements[r.id ?? '']?.sheetId ?? sl?.id,
        );
        break;
      }
      case 'draft_position_plan':
      case 'place_missing_points': {
        const an = analyse(p);
        const plan = sheetsInOrder(p).find((x) => x.kind === 'position-plan');
        if (!unplacedPoints(p, an).length) {
          skipped.push(
            tr(
              L,
              'Tous les points sont déjà sur le plan de position',
              'Every point is already on the position plan',
              'Alle punten staan al op het situatieschema',
            ),
          );
          break;
        }
        // A plan exists: the missing points go on it (rooms are the user's drawing there).
        if (plan) {
          const r = placeMissingOps(
            p,
            an,
            plan.id,
            a.type === 'place_missing_points' ? (a.points ?? undefined) : undefined,
          );
          push(
            r.ops,
            tr(
              L,
              `Plan « ${plan.name} » : ajouter ${listTags(r.placed, r.aside, L)}`,
              `Plan “${plan.name}”: add ${listTags(r.placed, r.aside, L)}`,
              `Plan “${plan.name}”: ${listTags(r.placed, r.aside, L)} toevoegen`,
            ),
            plan.id,
          );
          break;
        }
        const rooms =
          a.type === 'draft_position_plan'
            ? a.rooms.map((room) => ({
                name: room.name,
                roomType: room.roomType,
                tags: [
                  ...an.circuits
                    .filter((c) => room.circuits.some((x) => x.toUpperCase() === c.code.toUpperCase()))
                    .flatMap((c) => c.points.map((pt) => pt.element.tag)),
                  ...room.points.map((x) => x.toUpperCase()),
                ],
              }))
            : [];
        const r = draftPlanOps(p, an, rooms, tr(L, 'Plan de position', 'Position plan', 'Situatieschema'));
        const n = new Set(
          rooms.filter((room) => room.tags.some((x) => r.placed.includes(x))).map((x) => x.name),
        ).size;
        push(
          r.ops,
          tr(
            L,
            `Nouveau plan de position : ${n} pièce(s), ${r.placed.length} point(s) placés, ${r.aside.length} à positionner`,
            `New position plan: ${n} room(s), ${r.placed.length} point(s) placed, ${r.aside.length} to position`,
            `Nieuw situatieschema: ${n} ruimte(s), ${r.placed.length} punt(en) geplaatst, ${r.aside.length} te plaatsen`,
          ),
          r.sheetId,
        );
        break;
      }
    }
  }
  return { ops, summary, actionOf, skipped, ...(sheetId ? { sheetId } : {}) };
}

/**
 * " — ΔU 3,6 → 2,2 %, Iz 23 → 31 A": the editor's numbers before and after a circuit change, so
 * the proposal shows them rather than the model's estimate.
 */
function calcDelta(p: Project, ops: Op[], deviceId: Id, L: Locale): string {
  if (!ops.length) return '';
  const before = projectCalcs(p, analyse(p)).get(deviceId);
  const q = applyTransaction(p, { label: '', ops }).project;
  const after = projectCalcs(q, analyse(q)).get(deviceId);
  if (!before || !after) return '';
  const f = (n: number) => (L === 'en' ? n1(n) : n1(n).replace('.', ','));
  const parts: string[] = [];
  if (before.dropPct !== undefined && after.dropPct !== undefined && n1(before.dropPct) !== n1(after.dropPct))
    parts.push(`ΔU ${f(before.dropPct)} → ${f(after.dropPct)} %`);
  if (before.iz !== undefined && after.iz !== undefined && n1(before.iz) !== n1(after.iz))
    parts.push(`Iz ${f(before.iz)} → ${f(after.iz)} A`);
  return parts.length ? ` — ${parts.join(', ')}` : '';
}

/** "A4, E2 (aside: B3)": points added to a plan, those put aside for the user named apart. */
function listTags(placed: string[], aside: string[], L: Locale): string {
  const a = aside.length
    ? tr(
        L,
        ` (à positionner : ${aside.join(', ')})`,
        ` (to position: ${aside.join(', ')})`,
        ` (te plaatsen: ${aside.join(', ')})`,
      )
    : '';
  return (placed.length ? placed.join(', ') : '') + (placed.length ? a : a.replace(/^ \((.*)\)$/, '$1'));
}

// ---------- checking a proposal before the user sees it ----------

/** Findings that every new circuit has until it is drawn on the plan or given a length. */
const PENDING = /^R-(DOC|CALC-00|ML-01|PLAN-01)/;
const findingKey = (f: Finding) => `${f.ruleId}|${[...f.targets].sort().join(',')}`;

/** An earth electrode drawn without its value: the user measures it, not the assistant's fault. */
export const toMeasure = (project: Project, f: Finding) =>
  /EARTH-01$/.test(f.ruleId) &&
  f.targets.some((id) => {
    const v = project.elements[id]?.props.resistanceOhm;
    return v === undefined || v === '';
  });

/** Errors and warnings the proposal would add to the project (checked against its standard). */
export function checkProposal(project: Project, result: ActionResult): Finding[] {
  if (!result.ops.length) return [];
  const before = new Set(checkProject(project).map(findingKey));
  const after = applyTransaction(project, { label: '', ops: result.ops }).project;
  return checkProject(after).filter(
    (f) =>
      f.severity !== 'info' && !PENDING.test(f.ruleId) && !toMeasure(after, f) && !before.has(findingKey(f)),
  );
}

/** Message sent back to the model when its proposal fails the check (the user does not see it). */
export function proposalFeedback(findings: Finding[], skipped: string[], notes: string[] = []): string {
  const lines = [
    '[Automatic check by the editor. The user does not see this message.] Your proposal has not been applied.',
  ];
  for (const n of notes) lines.push(n);
  if (findings.length) {
    lines.push('Checked against the project’s standard, it would cause:');
    for (const f of findings) lines.push(`- ${f.severity} ${f.ruleId}: ${f.message.en}`);
  }
  if (skipped.length) {
    lines.push('These actions could not be applied:');
    for (const s of skipped) lines.push(`- ${s}`);
  }
  lines.push(
    'Answer again with the edit_project tool. Fix the actions if you can without changing what the user asked ' +
      '(for example split a circuit that has too many points). Otherwise keep the actions and explain the problem ' +
      'plainly in "reply", or ask the user. Never describe a change that is not in the actions.',
  );
  return lines.join('\n');
}

/**
 * The rating change the user asked for ("passe le circuit B en 16 A", "change circuit B to C10",
 * "zet kring A op 20 A"), or null when the message names no circuit and rating.
 */
export function requestedRating(
  text: string,
): { code: string; ratingA: number; curve: 'B' | 'C' | 'D' | null } | null {
  // A quoted finding ("Corrige ce problème : « Circuit C : Ib 26,1 A > In 20 A »") asks for no rating.
  const own = text.replace(/«[^»]*»|“[^”]*”|"[^"]*"/g, ' ');
  const c = /\b(?:circuit|kring|stroomkring)\s+([a-z])\b(.*)$/i.exec(own);
  if (!c) return null;
  // Not the decimals of another number ("26,1 A").
  const r = /\b([BCD])\s?(\d{1,2})\b|(?<![\d.,])(\d{1,2})\s?A\b/i.exec(c[2]!);
  if (!r) return null;
  return {
    code: c[1]!.toUpperCase(),
    ratingA: Number(r[2] ?? r[3]),
    curve: r[1] ? (r[1].toUpperCase() as 'B' | 'C' | 'D') : null,
  };
}

/**
 * When the user asked for a rating the circuit's cable allows (see MAX_RATING_A) and the answer
 * does not apply it, a note for the model saying the change is compliant; null otherwise.
 * (Models made cautious about cables sometimes refuse lowering a rating.)
 */
export function missedCompliantRating(
  project: Project,
  actions: AssistAction[],
  userText: string,
): string | null {
  const want = requestedRating(userText);
  if (!want) return null;
  const c = analyse(project).circuits.find((x) => x.code.toUpperCase() === want.code);
  const max = c ? MAX_RATING_A[String(c.cable.sectionMm2)] : undefined;
  if (!c || !max || want.ratingA > max) return null;
  const done = actions.some(
    (a) =>
      a.type === 'update_circuit' &&
      a.code.toUpperCase() === want.code &&
      a.ratingA === want.ratingA &&
      (!want.curve || a.curve === want.curve),
  );
  if (done) return null;
  const sec = String(c.cable.sectionMm2);
  return (
    `The user asked to set circuit ${want.code} to ${want.curve ?? ''}${want.ratingA} A. Its cable is ` +
    `${sec} mm², whose maximum rating is ${max} A, so ${want.ratingA} A is compliant and needs no cable ` +
    `change. Apply it with update_circuit (code "${want.code}", ratingA ${want.ratingA}` +
    `${want.curve ? `, curve "${want.curve}"` : ''}, other fields null).`
  );
}

export interface CheckedAssist extends AssistResponse {
  result: ActionResult;
  /** Problems the final proposal still has; shown in the preview. */
  findings: Finding[];
  /** True when the model was asked to revise its first proposal. */
  revised: boolean;
}

/**
 * Ask the assistant, check its proposal with the rule pack, and when it breaks a rule or contains
 * actions that cannot be applied, send the findings back once so it can fix them or explain.
 * `ask` is the transport (server call or demo parser); a failed retry keeps the first answer.
 */
/** Does the user's message talk about a cable section ("3G6", "6 mm²", "câble", "kabel"…)? */
export const mentionsCable = (text: string) =>
  /\d\s?G\s?\d|\d+(?:[.,]\d+)?\s?mm|\b(c[âa]bles?|cables?|kabels?|section|sectie|doorsnede|xvb|exvb|vob|r2v|ymvk)\b/i.test(
    text,
  );

/**
 * Changing a circuit's cable section means rewiring it, so the assistant may only do it when the
 * user's message mentions the cable. Other cable changes are removed from the actions (the rating
 * change stays, so the rule check reports it) and listed in `notes` for the user and the model.
 */
/** A "circuit" that is really the board's main switch or RCD (models add one to an empty board). */
const STANDS_IN_FOR_MAIN =
  /^\s*(g[ée]n[ée]ral|main|hoofd|interrupteur|disjoncteur (g[ée]n[ée]ral|principal|de branchement)|algemeen)/i;
const genericOnly = (c: DescribedCircuit) =>
  c.points.length > 0 &&
  c.points.every((p) => p.kind === 'appliance' && (!p.symbolId || p.symbolId === 'iec.appliance'));

/** Cable types a user may name ("en R2V", "XVB 3G2,5"). */
const CABLE_TYPES = /\b(xvb|exvb|xgb|vob|vvb|vtmb|r2v|u1000|h07v-?[ru]|ymvk|vmvk|xmvk|nym)\b/i;
const STANDARD_CABLE: Record<string, string> = { rgie: 'XVB', nfc15100: 'R2V', nen1010: 'YMvK' };

/** The cable type most used in the project's circuits, or its standard's usual one. */
export function usualCableType(project: Project): string {
  const count = new Map<string, number>();
  for (const c of Object.values(project.circuits)) {
    const t = c.cable?.type;
    if (t) count.set(t, (count.get(t) ?? 0) + 1);
  }
  const best = [...count].sort((a, b) => b[1] - a[1])[0]?.[0];
  return best ?? STANDARD_CABLE[project.meta.standard ?? 'rgie'] ?? 'XVB';
}

/** A request to fix problems in general ("corrige les erreurs"): any circuit may change. */
const FIX_WORDS =
  /\b(corrige\w*|r[ée]pare\w*|fix\w*|repair\w*|herstel\w*|verbeter\w*|los\s+\w+\s+op|probl[eè]mes?|problems?|erreurs?|errors?|fouten?|constats?|findings?|bevindingen)\b/i;

const fold = (t: string) =>
  t
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * namesCircuit, or the bare letter in capitals ("passe B en 10 A"), but not a unit ("10 A"), a
 * curve and rating ("C20") or an RCD type ("type A").
 */
function refersTo(text: string, code: string, label: string): boolean {
  if (namesCircuit(text, code, label)) return true;
  const bare = new RegExp(`(?<![A-Za-z0-9]|\\d\\s|type\\s)${code.toUpperCase()}(?![A-Za-z0-9])`);
  return bare.test(text);
}

/** Does the request name this circuit: "circuit G", "kring G", or a word of its label ("lave-vaisselle")? */
export function namesCircuit(text: string, code: string, label: string): boolean {
  const t = fold(text);
  // "circuit G", "circuits A et B", "kringen A, B en C"
  for (const m of t.matchAll(
    /\b(?:circuits?|kring(?:en)?|groupes?|groups?)\s+([a-z]\b(?:\s*(?:,|et|and|en|&)\s*[a-z]\b)*)/g,
  ))
    if (m[1]!.split(/\s*(?:,|et|and|en|&)\s*/).includes(code.toLowerCase())) return true;
  // Generic words ("prises" of "Prises séjour") count only when the label has nothing else.
  const words = fold(label)
    .split(/[^a-z0-9-]+/)
    .filter((w) => w.length >= 4);
  const specific = words.filter(
    (w) => !/^(prises?|sockets?|circuit|eclairage|lighting|stopcontacten|verlichting)$/.test(w),
  );
  return (specific.length ? specific : words).some((w) => t.includes(w));
}

export function guardActions(
  project: Project,
  actions: AssistAction[],
  userText: string,
  locale?: Locale,
  /** Every user message of the conversation (rooms named in an earlier answer count). */
  userTexts: string[] = [userText],
  /** The finding a "Fix with the assistant" request is about. */
  fix?: FixTarget,
): { actions: AssistAction[]; notes: string[] } {
  const L = locale ?? project.meta.locale;
  const an = analyse(project);
  const circuits = an.circuits;
  const scope = fixScope(an, fix);
  const fixAllows = (code: string, what: 'cable' | 'rating') => !!scope.get(code.toUpperCase())?.has(what);
  const notes: string[] = [];
  const cableAsked = mentionsCable(userText);
  const twoCoresAsked = /\b2\s*[gx×]\s*\d/i.test(userText);
  // Powers and lengths feed Ib and ΔU: kept only when the user stated some (the model otherwise
  // fills in guesses such as a 12 kW oven, and the checks then flag a problem that is not there).
  const powerAsked = /\d\s*(k?w|kilowatts?|watts?)\b/i.test(userText);
  const ratingAsked = /\b[BCD]\s?\d{1,2}\b|\b\d{1,3}\s?A\b/i.test(userText);
  const lengthAsked = /\d\s*(m|mètres?|metres?|meters?|meter)\b/i.test(userText);
  // Rooms and measurements may come in an earlier answer ("la terre fait 22 Ω", then "oui").
  const kaAsked = /\d\s*kA\b/i.test(userTexts.join('\n'));
  const ohmAsked = /\d\s*(Ω|ohms?\b)/i.test(userTexts.join('\n'));
  // The cable type of the project (or of its standard), unless the user named one: the model
  // sometimes writes the type of another country (R2V in an RGIE project).
  const cableType = usualCableType(project);
  const typeAsked = CABLE_TYPES.test(userTexts.join('\n'));
  const ownType = <C extends { type: string } | null | undefined>(cb: C): C =>
    cb && !typeAsked && cb.type.toUpperCase() !== cableType.toUpperCase() ? { ...cb, type: cableType } : cb;
  const stated = (c: DescribedCircuit): DescribedCircuit => ({
    ...c,
    points: powerAsked ? c.points : c.points.map(({ powerKw: _, ...pt }) => pt),
    cable: c.cable && !lengthAsked ? (({ lengthM: _, ...cb }) => cb)(c.cable) : c.cable,
  });
  const out = actions.flatMap((original): AssistAction[] => {
    // Lengths of update_circuit too: only when the user gave one.
    const a0 =
      original.type === 'update_circuit' && original.cable?.lengthM && !lengthAsked
        ? { ...original, cable: (({ lengthM: _, ...cb }) => cb)(original.cable) }
        : original;
    // A breaking capacity is read on the device: only the one the user gave.
    const a1 = a0.type === 'update_circuit' && a0.breakingKa && !kaAsked ? { ...a0, breakingKa: null } : a0;
    const a =
      a1.type === 'update_circuit' && a1.cable && ownType(a1.cable) !== a1.cable
        ? { ...a1, cable: ownType(a1.cable) }
        : a1;
    // Deleting is only for circuits the user named (by letter or by what they feed): never "all".
    if (a.type === 'delete_circuit') {
      const c = circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
      if (!c || namesCircuit(userText, c.code, c.circuit?.label ?? '')) return [a];
      notes.push(
        tr(
          L,
          `Circuit ${c.code} non supprimé : la demande ne le nomme pas`,
          `Circuit ${c.code} not deleted: the request does not name it`,
          `Kring ${c.code} niet verwijderd: de vraag noemt hem niet`,
        ),
      );
      return [];
    }
    // Sockets or lights on the circuit of a fixed appliance (the cooker's) are never right
    // (R-CIR-03): the model reaches for it when a pasted line repeats the appliance.
    if (a.type === 'add_points' && a.points.some((x) => x.kind === 'socket' || x.kind === 'light')) {
      const c = circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
      if (c && c.points.some((pt) => DEDICATED.has(pt.element.symbolId))) {
        notes.push(
          tr(
            L,
            `Circuit ${c.code} : pas de prise ni de point lumineux sur le circuit d’un appareil fixe (il doit rester seul)`,
            `Circuit ${c.code}: no sockets or lights on the circuit of a fixed appliance (it must stay alone)`,
            `Kring ${c.code}: geen stopcontacten of lichtpunten op de kring van een vast toestel (het moet alleen blijven)`,
          ),
        );
        return [];
      }
    }
    // Moving, changing or splitting a circuit the conversation never names is the model's own
    // initiative (it "fixes" a finding while adding something else): left out, like a delete.
    if (
      !fix &&
      !FIX_WORDS.test(userTexts.join('\n')) &&
      (a.type === 'move_circuit' || a.type === 'update_circuit' || a.type === 'split_circuit')
    ) {
      const c = circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
      if (c && ![userText, ...userTexts].some((t) => refersTo(t, c.code, c.circuit?.label ?? ''))) {
        notes.push(
          tr(
            L,
            `Circuit ${c.code} non modifié : la demande ne le nomme pas`,
            `Circuit ${c.code} not changed: the request does not name it`,
            `Kring ${c.code} niet gewijzigd: de vraag noemt hem niet`,
          ),
        );
        return [];
      }
    }
    if (a.type === 'add_circuits' || a.type === 'add_rcd_group') {
      const keep = a.circuits.filter((c) => !(STANDS_IN_FOR_MAIN.test(c.label) && genericOnly(c)));
      for (const c of a.circuits.filter((x) => !keep.includes(x)))
        notes.push(
          tr(
            L,
            `« ${c.label} » non ajouté comme circuit : l’interrupteur général et le différentiel général font partie du tableau`,
            `“${c.label}” not added as a circuit: the main switch and main RCD are part of the board`,
            `“${c.label}” niet als kring toegevoegd: de hoofdschakelaar en het hoofddifferentieel horen bij het bord`,
          ),
        );
      // A cable without its protective conductor (XVB 2G…) is never right for a final circuit:
      // the model writes it when it counts only the live conductors. Fixed silently (the
      // proposal shows 3G), unless the user typed 2G themselves.
      const withEarth = keep
        .map(stated)
        .map((c) => (ownType(c.cable) === c.cable ? c : { ...c, cable: ownType(c.cable) }))
        .map((c) =>
          !c.cable || c.cable.cores >= 3 || twoCoresAsked ? c : { ...c, cable: { ...c.cable, cores: 3 } },
        );
      const changed = withEarth.some((c, i) => c !== keep[i]);
      if (keep.length === a.circuits.length && !changed) return [a];
      return keep.length ? [{ ...a, circuits: withEarth }] : [];
    }
    // A rating change the user did not ask for ("mets le circuit D en 3G10" → 40 A): left out,
    // like an unasked cable change, so the user decides on the protection.
    if (a.type === 'update_circuit' && a.ratingA !== null && !ratingAsked && !fixAllows(a.code, 'rating')) {
      const c = circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
      if (c && c.ratingA !== a.ratingA) {
        notes.push(
          tr(
            L,
            `Circuit ${c.code} : calibre ${c.ratingA ?? '?'} → ${a.ratingA} A non appliqué (non demandé)`,
            `Circuit ${c.code}: rating ${c.ratingA ?? '?'} → ${a.ratingA} A not applied (not asked for)`,
            `Kring ${c.code}: ${c.ratingA ?? '?'} → ${a.ratingA} A niet toegepast (niet gevraagd)`,
          ),
        );
        const rest = { ...a, ratingA: null };
        if (rest.label === null && rest.curve === null && rest.cable === null) return [];
        return cableAsked || fixAllows(a.code, 'cable') || !rest.cable ? [rest] : guardCable(rest);
      }
    }
    if (a.type === 'draft_position_plan') return [namedRooms(a)];
    // A resistance is a measurement: only the one the user gave.
    if (a.type === 'set_earth_electrode' && a.resistanceOhm !== null && !ohmAsked)
      return [{ ...a, resistanceOhm: null }];
    if (a.type !== 'update_circuit') return [a];
    return cableAsked || fixAllows(a.code, 'cable') || !a.cable ? [a] : guardCable(a);
  });
  return { actions: boardForEmpty(project, out), notes };

  /**
   * Rooms of a drafted plan that neither the user nor the labels of the circuits put in them
   * name are the model's guess: left out, their points put aside for the user.
   */
  function namedRooms(a: Extract<AssistAction, { type: 'draft_position_plan' }>): AssistAction {
    const said = fold(userTexts.join('\n'));
    const rooms = a.rooms.filter((room) => {
      const codes = new Set([
        ...room.circuits.map((x) => x.toUpperCase()),
        ...room.points.map((x) => x.toUpperCase().replace(/\d+$/, '')),
      ]);
      const labels = fold(
        circuits
          .filter((c) => codes.has(c.code.toUpperCase()))
          .map((c) => c.circuit?.label ?? '')
          .join('\n'),
      );
      const words = fold(room.name)
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length >= 4 && !ROOM_FILLER.test(w));
      // A cooker or a dishwasher says "kitchen" as clearly as a label does.
      const kitchen =
        room.roomType === 'kitchen' &&
        circuits.some(
          (c) =>
            codes.has(c.code.toUpperCase()) &&
            c.points.some((pt) => KITCHEN_APPLIANCES.has(pt.element.symbolId)),
        );
      const named = kitchen || words.some((w) => said.includes(w) || labels.includes(w));
      if (!named)
        notes.push(
          tr(
            L,
            `Pièce « ${room.name} » non dessinée : ni la demande ni ses circuits ne la nomment (ses points sont mis de côté)`,
            `Room “${room.name}” not drawn: neither the request nor its circuits name it (its points are put aside)`,
            `Ruimte “${room.name}” niet getekend: noch de vraag noch haar kringen noemen haar (haar punten worden apart gezet)`,
          ),
        );
      return named;
    });
    return rooms.length === a.rooms.length ? a : { ...a, rooms };
  }

  function guardCable(a: Extract<AssistAction, { type: 'update_circuit' }>): AssistAction[] {
    if (!a.cable) return [a];
    const c = circuits.find((x) => x.code.toUpperCase() === a.code.toUpperCase());
    if (!c || !c.cable.sectionMm2 || a.cable.sectionMm2 === c.cable.sectionMm2) return [a];
    const from = String(c.cable.sectionMm2).replace('.', ',');
    const to = String(a.cable.sectionMm2).replace('.', ',');
    notes.push(
      tr(
        L,
        `Circuit ${c.code} : câble ${from} → ${to} mm² non appliqué (non demandé : cela implique de recâbler)`,
        `Circuit ${c.code}: cable ${from} → ${to} mm² not applied (not asked for: it means rewiring)`,
        `Kring ${c.code}: kabel ${from} → ${to} mm² niet toegepast (niet gevraagd: dat betekent herbekabelen)`,
      ),
    );
    return [{ ...a, cable: null }];
  }
}

const KITCHEN_APPLIANCES = new Set(['iec.cooker', 'iec.oven', 'iec.dishwasher']);
/**
 * Circuits added to an empty diagram would hang from nothing (R-RCD-00): they become the board
 * the RGIE asks for, a 300 mA main RCD with the dry circuits and a 30 mA one with the wet ones
 * (the model answered with add_circuits and had to be asked again).
 */
function boardForEmpty(project: Project, actions: AssistAction[]): AssistAction[] {
  const sl = singleLine(project);
  if (!sl || !supplyChainOnly(project, sl.id)) return actions;
  const adds = actions.filter(
    (a): a is Extract<AssistAction, { type: 'add_circuits' }> => a.type === 'add_circuits',
  );
  if (!adds.length) return actions;
  if (actions.some((a) => a.type === 'add_rcd_group' && a.sensitivityMa >= 100)) return actions;
  const circuits = adds.flatMap((a) => a.circuits);
  const wet = circuits.filter((c) => c.rcdMa !== null && c.rcdMa <= 30);
  const dry = circuits.filter((c) => !wet.includes(c));
  const group = (sensitivityMa: number, cs: DescribedCircuit[]): AssistAction => ({
    type: 'add_rcd_group',
    sensitivityMa,
    ratingA: null,
    rcdType: 'A',
    circuits: cs,
  });
  const board = [group(300, dry), ...(wet.length ? [group(30, wet)] : [])];
  const first = actions.indexOf(adds[0]!);
  return actions.flatMap((a, i) => (i === first ? board : a.type === 'add_circuits' ? [] : [a]));
}

const CONFIRM =
  /^\s*(oui|ok|okay|d'accord|d’accord|vas-y|allez-y|go|parfait|exact|c'est ça|yes|yep|sure|please do|ja|jawel|goed|doe maar|graag|prima|akkoord)\b/i;

/**
 * What the user asks, for the guards: their last message, and when it only confirms ("oui",
 * "ja, doe maar", "yes please, in 6 mm²") the question it answers, so a change the assistant
 * offered ("…passer en 6 mm² ?") counts as asked.
 */
export function requestText(messages: AssistMessage[]): string {
  const i = messages.map((m) => m.role).lastIndexOf('user');
  const last = messages[i]?.text ?? '';
  const prev = messages[i - 1];
  if (prev?.role === 'assistant' && last.length <= 80 && CONFIRM.test(last)) return `${prev.text}\n${last}`;
  return last;
}

/** Words of a room name that name no room in particular. */
const ROOM_FILLER = /^(piece|pieces|room|rooms|ruimte|ruimtes|zone|espace|area|kamer)$/;
// Words that say which points or how many: without any of them, a count of sockets or lights
// in the answer is the model's guess ("fais la cuisine" → 4 sockets).
const POINT_WORDS =
  /\b(prises?|sockets?|outlets?|stopcontact\w*|contactdoos\w*|[ée]clairage|lumi[eè]re\w*|lights?|lighting|lamp\w*|licht\w*|verlicht\w*|luminaires?|plafonniers?|appliques?|spots?|points?|punt(en)?|interrupteurs?|switch(es)?|schakelaars?)\b/i;
const COUNT_WORDS =
  /\d|\b(deux|trois|quatre|cinq|six|sept|huit|neuf|dix|onze|douze|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twee|drie|vier|vijf|zes|zeven|acht|negen|tien|elf|twaalf)\b/i;

/**
 * Feedback when the answer adds sockets or lights the user never asked for, in number or in
 * kind: the model should ask instead. Null when the request says what to add.
 */
export function guessedPoints(actions: AssistAction[], userTexts: string[]): string | null {
  const said = userTexts.join('\n');
  if (POINT_WORDS.test(said) || COUNT_WORDS.test(said)) return null;
  const guessed = actions.some((a) =>
    (a.type === 'add_circuits' || a.type === 'add_rcd_group'
      ? a.circuits.flatMap((c) => c.points)
      : a.type === 'add_points'
        ? a.points
        : []
    ).some((x) => x.kind === 'socket' || x.kind === 'light'),
  );
  if (!guessed) return null;
  return (
    'The user did not say what to add (which sockets, lights or appliances, or how many). Do not guess: ' +
    'return no actions and ask in "reply" what they want, for example how many sockets and which appliances.'
  );
}

const NUMBER: Record<string, number> = {
  un: 1,
  une: 1,
  one: 1,
  een: 1,
  deux: 2,
  two: 2,
  twee: 2,
  trois: 3,
  three: 3,
  drie: 3,
  quatre: 4,
  four: 4,
  vier: 4,
  cinq: 5,
  five: 5,
  vijf: 5,
  six: 6,
  zes: 6,
  sept: 7,
  seven: 7,
  zeven: 7,
  huit: 8,
  eight: 8,
  acht: 8,
  neuf: 9,
  nine: 9,
  negen: 9,
  dix: 10,
  ten: 10,
  tien: 10,
  douze: 12,
  twelve: 12,
  twaalf: 12,
};
const COUNTED: Record<'socket' | 'light', RegExp> = {
  socket: /(\d+|[a-zéè]+)\s+(?:prises?|sockets?|outlets?|stopcontact(?:en)?|contactdozen?)\b/gi,
  light:
    /(\d+|[a-zéè]+)\s+(?:points?\s+lumineux|points?\s+d['’]éclairage|lights?|lighting\s+points?|lichtpunt(?:en)?|luminaires?|spots?)\b/gi,
};

/**
 * Feedback when an answer adds more sockets or lights than the request counts ("6 prises et 8
 * points lumineux" read as 6 circuits of 8 sockets and 8 circuits of 8 lights). Null when the
 * request counts none, or the answer stays within it.
 */
export function overCounted(actions: AssistAction[], userText: string): string | null {
  const notes: string[] = [];
  for (const kind of ['socket', 'light'] as const) {
    let said = 0;
    for (const m of userText.matchAll(COUNTED[kind])) {
      const n = /^\d+$/.test(m[1]!) ? Number(m[1]) : NUMBER[m[1]!.toLowerCase()];
      if (n) said += n;
    }
    if (!said) continue;
    const added = actions
      .flatMap((a) =>
        a.type === 'add_circuits' || a.type === 'add_rcd_group'
          ? a.circuits.flatMap((c) => c.points)
          : a.type === 'add_points'
            ? a.points
            : [],
      )
      .filter((x) => x.kind === kind)
      .reduce((n, x) => n + x.count, 0);
    const word = kind === 'socket' ? 'sockets' : 'lights';
    // New circuits that hold only this kind: "6 prises" is one circuit, not six of one socket.
    const circuitsOfKind = actions
      .flatMap((a) => (a.type === 'add_circuits' || a.type === 'add_rcd_group' ? a.circuits : []))
      .filter((c) => c.points.length > 0 && c.points.every((x) => x.kind === kind)).length;
    const circuitsSaid =
      /\d+\s*(?:circuits?|kringen|groupes?)\b|\b(?:deux|trois|two|three|twee|drie)\s+(?:circuits|kringen)\b/i.test(
        userText,
      );
    if (added > said)
      notes.push(
        `The user asked for ${said} ${word} in all, and your actions add ${added}. ` +
          `"6 prises et 8 points lumineux" is one circuit of 6 sockets and one of 8 lights, not 6 or 8 circuits.`,
      );
    else if (!circuitsSaid && circuitsOfKind > Math.max(1, Math.ceil(said / 8)))
      notes.push(
        `The user asked for ${said} ${word}, and your actions spread them over ${circuitsOfKind} circuits. ` +
          `"6 prises et 8 points lumineux" is one circuit of 6 sockets and one of 8 lights (at most 8 points per circuit), not one circuit per point.`,
      );
    else if (added > 0 && added < said)
      notes.push(`The user asked for ${said} ${word} in all, and your actions add only ${added}.`);
  }
  return notes.length ? `${notes.join('\n')} Add what was asked, no more and no less.` : null;
}

/**
 * Feedback when the user asks why something is flagged and the answer edits the project: a
 * question gets an explanation and an offer ("Voulez-vous que je… ?"), the change waits for a yes.
 */
export function answeredQuestion(actions: AssistAction[], lastUserText: string): string | null {
  if (!actions.length || !WHY.test(lastUserText)) return null;
  return (
    'The user asked a question; they did not ask for a change. Explain in "reply" and end with an offer ' +
    '("Voulez-vous que je … ?"), with no actions. The change is made once they say yes.'
  );
}
const WHY = /^\s*(pourquoi|why|waarom|how come|comment se fait-il)\b/i;

/** Words that make a circuit label a room ("Prises séjour", "Salle de bain", "Badkamer"). */
const ROOM_WORDS =
  /\b(s[ée]jour|salon|living|woonkamer|cuisine|kitchen|keuken|chambres?|bedrooms?|slaapkamers?|salle\s+de\s+(?:bains?|douche)|bathroom|shower\s+room|badkamer|douche|buanderie|laundry|wasplaats|wasruimte|garage|bureau|office|study|hall|entr[ée]e|inkom|grenier|attic|zolder|cave|cellar|kelder|atelier|workshop|werkplaats|terrasse|terrace|terras|jardin|garden|tuin|wc|toilettes?|toilet)\b/i;

/**
 * Feedback when a drafted position plan leaves out circuits whose label names a room ("Salle de
 * bain"): the label says where they go as clearly as the user would.
 */
export function missedRooms(project: Project, actions: AssistAction[]): string | null {
  const plans = actions.filter(
    (a): a is Extract<AssistAction, { type: 'draft_position_plan' }> => a.type === 'draft_position_plan',
  );
  if (!plans.length) return null;
  const placed = new Set(
    plans.flatMap((a) =>
      a.rooms.flatMap((r) => [
        ...r.circuits.map((x) => x.toUpperCase()),
        ...r.points.map((x) => x.toUpperCase().replace(/\d+$/, '')),
      ]),
    ),
  );
  const missed = analyse(project).circuits.filter(
    (c) => !placed.has(c.code.toUpperCase()) && ROOM_WORDS.test(c.circuit?.label ?? ''),
  );
  if (!missed.length) return null;
  return (
    `These circuits name their room in their label but are in no room of your plan: ` +
    missed.map((c) => `${c.code} "${c.circuit?.label ?? ''}"`).join(', ') +
    '. Put each in the room its label names (add the room if needed). Keep the other circuits as they are.'
  );
}

/**
 * A drafted plan when neither the circuits' labels nor the user name a room: only appliances (a
 * hob, a dishwasher) hint at one. The model should ask which rooms there are.
 */
export function unnamedPlan(project: Project, actions: AssistAction[], userTexts: string[]): boolean {
  if (!actions.some((a) => a.type === 'draft_position_plan')) return false;
  if (userTexts.some((t) => ROOM_WORDS.test(t))) return false;
  return !analyse(project).circuits.some((c) => ROOM_WORDS.test(c.circuit?.label ?? ''));
}

/** A list of at least two circuits (one line asking for one thing is an ordinary request). */
const pastedList = (text: string) => {
  const listed = pastedCircuitList(text.trim());
  return listed && listed.length >= 2 ? listed : null;
};

/** New circuits an answer adds. */
const newCircuits = (actions: AssistAction[]) =>
  actions.reduce(
    (n, a) => n + (a.type === 'add_circuits' || a.type === 'add_rcd_group' ? a.circuits.length : 0),
    0,
  );
const REUSES = new Set(['split_circuit', 'add_points', 'update_circuit', 'move_circuit']);

/**
 * Feedback when the user pasted a list of circuits (one per line) and the answer reuses existing
 * circuits or adds fewer than listed: each line is a new circuit, even one like an existing one.
 */
export function pastedListNote(actions: AssistAction[], lastUserText: string): string | null {
  const listed = pastedList(lastUserText);
  if (!listed || !actions.length) return null;
  if (newCircuits(actions) >= listed.length && !actions.some((a) => REUSES.has(a.type))) return null;
  return (
    `The user pasted a list of ${listed.length} circuits: each line is a NEW circuit. Add one circuit per line with ` +
    'add_circuits (or add_rcd_group), and do not split, extend or change existing circuits for it. If a line ' +
    'looks like a circuit that already exists (a second hob), still add it and say in "reply" which existing ' +
    'circuit it resembles, so the user can remove one.'
  );
}

/** A plan the model drafts or completes: the reply must say its positions are to be moved. */
const DRAFT_SAID =
  /brouillon|provisoire|[àa] d[ée]placer|[àa] ajuster|draft|to move|voorlopig|ontwerp|te verplaatsen/i;

/**
 * The reply as the editor shows it:
 * - the model's Iz and max-rating figures are replaced by the editor's (see figures.ts), when
 *   the project is given;
 * - when the answer draws points on a position plan and the model did not say so, the editor
 *   adds that the positions are a draft to move to their real place.
 */
export function finishReply(
  reply: string,
  actions: AssistAction[],
  locale: Locale,
  project?: Project,
): string {
  if (project) reply = correctFigures(reply, project, locale).reply;
  const placed = actions.some((a) => a.type === 'draft_position_plan' || a.type === 'place_missing_points');
  if (!placed || DRAFT_SAID.test(reply)) return reply;
  return `${reply.trimEnd()} ${tr(
    locale,
    'Les positions sont un brouillon : déplacez chaque point à sa vraie place sur le plan.',
    'The positions are a draft: move each point to its real place on the plan.',
    'De posities zijn een ontwerp: verplaats elk punt naar zijn echte plaats op het plan.',
  )}`;
}

/**
 * Fixing "Ib > In" (R-CALC-01) by raising the rating or the cable before the user confirmed the
 * power: the loads may be overstated, so the model should ask first.
 */
export function powerFirst(actions: AssistAction[], fix?: FixTarget, answered = false): string | null {
  if (fix?.ruleId !== 'R-CALC-01' || answered) return null;
  const changes = actions.some(
    (a) => a.type === 'update_circuit' && (a.ratingA !== null || a.cable !== null),
  );
  if (!changes) return null;
  return (
    'Before raising the rating or the cable of a circuit whose design current exceeds its rating, ask whether ' +
    'the power entered is right (and whether all loads run at once). Return no actions; end "reply" with that question.'
  );
}

/** Everything the editor sends back about an answer besides broken rules and skipped actions. */
export function retryNotes(
  project: Project,
  actions: AssistAction[],
  reply: string,
  /** The request, with the question a "oui" answers (requestText). */
  userText: string,
  userTexts: string[],
  /** What the user actually typed last. */
  lastUserText: string,
  locale?: Locale,
  /** The finding a "Fix with the assistant" request is about, and whether the user already answered. */
  fix?: FixTarget,
  answered = false,
): string[] {
  return [
    powerFirst(actions, fix, answered),
    missedCompliantRating(project, actions, userText),
    guessedPoints(actions, userTexts),
    overCounted(actions, userText),
    answeredQuestion(actions, lastUserText),
    missedRooms(project, actions),
    pastedListNote(actions, lastUserText),
    unnamedPlan(project, actions, userTexts)
      ? 'No circuit label and nothing the user wrote names a room; an appliance (a hob, a dishwasher) does not ' +
        'either. Return no actions and end "reply" with the question: which rooms are there, and which circuits serve each?'
      : null,
    // The language of what the user typed, not of the question a "oui" answers.
    languageNote(reply, lastUserText, locale),
  ].filter((n): n is string => n !== null);
}

const WET_SYMBOLS = new Set(['iec.washing-machine', 'iec.dryer', 'iec.dishwasher']);

/**
 * What the editor proposes itself when the revised answer still misses the request: its own
 * question when the model guesses points or rooms, the pasted list as typed when the model still
 * reuses existing circuits. Null keeps the revised answer.
 */
export function afterRetry(
  project: Project,
  actions: AssistAction[],
  userTexts: string[],
  lastUserText: string,
  locale?: Locale,
  fix?: FixTarget,
  answered = false,
): AssistResponse | null {
  const L = locale ?? project.meta.locale;
  if (guessedPoints(actions, userTexts)) return { reply: askWhatToAdd(L), actions: [] };
  if (powerFirst(actions, fix, answered)) return { reply: askPower(L), actions: [] };
  if (unnamedPlan(project, actions, userTexts)) return { reply: askRooms(L), actions: [] };
  const listed = pastedList(lastUserText);
  if (listed && pastedListNote(actions, lastUserText) && listed.every((c) => c.points.length))
    return {
      reply: tr(
        L,
        'Je propose d’ajouter ces circuits, un par ligne de votre liste (valeurs à vérifier) :',
        'I suggest adding these circuits, one per line of your list (values to check):',
        'Ik stel voor deze kringen toe te voegen, één per regel van uw lijst (waarden na te kijken):',
      ),
      actions: [
        {
          type: 'add_circuits',
          circuits: listed.map((c) =>
            c.rcdMa === null && c.points.some((x) => x.symbolId && WET_SYMBOLS.has(x.symbolId))
              ? { ...c, rcdMa: 30 }
              : c,
          ),
        },
      ],
    };
  return null;
}

/** The editor's own question when the model raises a rating before the power is confirmed. */
export const askPower = (L: Locale) =>
  tr(
    L,
    'Avant de changer le calibre : la puissance indiquée est-elle la bonne, et tous les appareils de ce circuit fonctionnent-ils en même temps ? Si oui, je propose un calibre et une section adaptés (cela implique de recâbler).',
    'Before changing the rating: is the power entered right, and do all loads of this circuit run at once? If so, I will propose a suitable rating and section (which means rewiring).',
    'Voor ik het kaliber wijzig: klopt het opgegeven vermogen, en werken alle toestellen van deze kring tegelijk? Zo ja, dan stel ik een passend kaliber en een passende doorsnede voor (dat betekent herbekabelen).',
  );

/** The editor's own question when the model drafts rooms nothing names. */
export const askRooms = (L: Locale) =>
  tr(
    L,
    'Aucun circuit ne nomme sa pièce. Quelles pièces y a-t-il, et quels circuits desservent chacune ? Je dessinerai ensuite le plan de position.',
    'No circuit names its room. Which rooms are there, and which circuits serve each? I will then draft the position plan.',
    'Geen enkele kring noemt zijn ruimte. Welke ruimtes zijn er, en welke kringen voeden elk ervan? Dan teken ik het situatieschema.',
  );

/** What the editor says when the model still guesses after being told not to. */
export const askWhatToAdd = (L: Locale) =>
  tr(
    L,
    'Que faut-il ajouter : combien de prises et de points lumineux, et quels appareils (four, lave-vaisselle…) ?',
    'What should I add: how many sockets and lights, and which appliances (oven, dishwasher…)?',
    'Wat moet ik toevoegen: hoeveel stopcontacten en lichtpunten, en welke toestellen (oven, vaatwasser…)?',
  );

const STOPWORDS: Record<'fr' | 'nl' | 'en', RegExp> = {
  fr: /\b(je|le|la|les|des|du|un|une|et|est|pour|sous|dans|avec|propose|ajouter|circuit|prises?|différentiel|pas|vous)\b/gi,
  nl: /\b(ik|de|het|een|en|is|voor|onder|met|in|stel|voor|kring|stopcontacten|toevoegen|niet|u|je|van|op)\b/gi,
  en: /\b(the|and|is|for|under|with|suggest|add|sockets?|not|you|to|of|on|this|it)\b/gi,
};

/** The language of a text (French, Dutch or English), or null when too short to tell. */
export function textLanguage(text: string): 'fr' | 'nl' | 'en' | null {
  const scores = (Object.keys(STOPWORDS) as Array<'fr' | 'nl' | 'en'>).map(
    (l) => [l, text.match(STOPWORDS[l])?.length ?? 0] as const,
  );
  scores.sort((a, b) => b[1] - a[1]);
  const [best, second] = scores;
  return best![1] >= 3 && best![1] >= 2 * second![1] ? best![0] : null;
}

const LANGUAGE_NAME = { fr: 'French', nl: 'Dutch', en: 'English', de: 'German' };
const OPENER = {
  fr: '« Je propose… »',
  nl: '"Ik stel voor…", never in French',
  en: '"I suggest…"',
  de: '„Ich schlage vor…“, never in French',
};

/**
 * Feedback when the reply is not in the user's language (the request's, or the interface's when
 * the request is too short to tell): Qwen sometimes answers Dutch requests in French.
 */
export function languageNote(reply: string, userText: string, locale?: Locale): string | null {
  const want = textLanguage(userText) ?? locale ?? null;
  // The opening phrase the prompt suggests ("Je propose…") gives away a reply that switches
  // language after a few words.
  const opener = /^\s*(je propose|je ne)\b/i.test(reply)
    ? 'fr'
    : /^\s*(ik stel voor|ik kan)\b/i.test(reply)
      ? 'nl'
      : /^\s*(i suggest|i can)\b/i.test(reply)
        ? 'en'
        : null;
  const got = opener ?? textLanguage(reply);
  if (!want || !got || want === got) return null;
  return `Your reply is in ${LANGUAGE_NAME[got]}, but the user writes in ${LANGUAGE_NAME[want]}: write "reply" in ${LANGUAGE_NAME[want]}, with the same actions.`;
}

export async function assistWithCheck(
  ask: (messages: AssistMessage[]) => Promise<AssistResponse>,
  project: Project,
  messages: AssistMessage[],
  sheetId?: Id,
  /** Off for the demo parser, which cannot read the feedback. */
  retry = true,
  /** Language of the summary, the notes and the editor's own question (the interface's). */
  locale?: Locale,
): Promise<CheckedAssist> {
  const userText = requestText(messages);
  const userTexts = [...messages.filter((m) => m.role === 'user').map((m) => m.text), userText];
  const fix = [...messages].reverse().find((m) => m.role === 'user')?.fix;
  // The editor's view of an answer: guarded actions, their ops, and the rule check.
  const evaluate = (r: AssistResponse) => {
    const g = guardActions(project, r.actions, userText, locale, userTexts, fix);
    const result = actionsToOps(project, g.actions, sheetId, locale);
    result.skipped.push(...g.notes);
    const reply = finishReply(r.reply, g.actions, locale ?? project.meta.locale, project);
    return { ...r, reply, actions: g.actions, result, findings: checkProposal(project, result) };
  };
  // An earlier answer of the assistant: the user is replying to it (the power confirmed, say).
  const answered = messages.some((m) => m.role === 'assistant');
  const first = evaluate(await ask(messages));
  const lastUserText = [...messages].reverse().find((m) => m.role === 'user')?.text ?? '';
  const notes = retryNotes(
    project,
    first.actions,
    first.reply,
    userText,
    userTexts,
    lastUserText,
    locale,
    fix,
    answered,
  );
  // An earth resistance the user measured above the limit is their value: the proposal shows the
  // finding and the reply warns; it is not the model's mistake to be revised.
  const measured = first.actions.some((x) => x.type === 'set_earth_electrode' && x.resistanceOhm !== null);
  const own = first.findings.filter((f) => !(measured && /EARTH-01$/.test(f.ruleId)));
  const broken = first.actions.length > 0 && (own.length > 0 || first.result.skipped.length > 0);
  if (!retry || (!broken && !notes.length)) return { ...first, revised: false };
  try {
    const proposed = `${first.reply}\n\nActions: ${JSON.stringify(first.actions)}`;
    const feedback = proposalFeedback(first.findings, first.result.skipped, notes);
    const second = evaluate(
      await ask([
        ...messages,
        { role: 'assistant', text: proposed.slice(0, 4000) },
        { role: 'user', text: feedback.slice(0, 4000) },
      ]),
    );
    const instead = afterRetry(project, second.actions, userTexts, lastUserText, locale, fix, answered);
    return instead ? { ...evaluate(instead), revised: true } : { ...second, revised: true };
  } catch {
    return { ...first, revised: false };
  }
}

// ---------- local (demo) intent parser ----------

const TEMPLATE_WORDS: Array<[RegExp, (typeof TEMPLATE_IDS)[number]]> = [
  [/borne de recharge|recharge|\bve\b|\bev\b|voiture|charger|wallbox|laadpaal|laadpunt/i, 'ev-charger'],
  [/photovolta|\bpv\b|solaire|solar|onduleur|inverter|zonnepan|omvormer/i, 'pv'],
  [/pompe à chaleur|pompe a chaleur|\bpac\b|heat pump|warmtepomp/i, 'heat-pump'],
  [/télérupteur|telerupteur|impulse|teleruptor/i, 'impulse-lighting'],
  [/volets?|shutters?|rolluik/i, 'shutters'],
];

/**
 * Rule-based stand-in for the model, used in demo mode. Understands a handful of requests
 * in French, Dutch and English; anything else gets a short help answer.
 */
const INSTRUCTION =
  /\b(ajout\w*|add|cr[ée]e\w*|create|voeg\w*|maak|passe|mets?|change|modifi\w*|set|zet|wijzig|verander|renomm\w*|rename|hernoem|g[ée]n[èe]re|generate|num[ée]rot\w*|number|nummer\w*|renum\w*|supprim\w*|retir\w*|enl[eè]v\w*|delete|remove|verwijder\w*|d[ée]plac\w*|move|verplaats\w*|fais|do|ins[èe]re|insert)\b/i;

const QUESTION =
  /\?|^\s*(combien|quel\w*|pourquoi|comment|how|what|which|why|hoeveel|welke?|wat|waarom|hoe)\b/i;

/**
 * The circuits of a pasted list (one per line, each with points, a rating or a cable) when the text
 * has no instruction in it; null otherwise.
 */
export function pastedCircuitList(text: string): DescribedCircuit[] | null {
  if (INSTRUCTION.test(text) || QUESTION.test(text)) return null;
  const lines = text
    .split(/[\n;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (!lines.length) return null;
  const circuits = parseCircuitText(text);
  return circuits.length === lines.length && circuits.every((c) => c.points.length || c.ratingA || c.cable)
    ? circuits
    : null;
}

export function parseAssistRequest(text: string, project: Project): AssistResponse {
  const L = project.meta.locale;
  const t = text.trim();
  // A pasted list of circuits with no instruction ("Cuisine : 6 prises 20 A 3G2,5" per line):
  // add them all, like the old "Describe circuits" dialog did.
  const listed = pastedCircuitList(t);
  if (listed)
    return {
      reply: tr(
        L,
        'Je propose d’ajouter ces circuits :',
        'I suggest adding these circuits:',
        'Ik stel voor deze kringen toe te voegen:',
      ),
      actions: [{ type: 'add_circuits', circuits: listed }],
    };
  const actions: AssistAction[] = [];
  const parts = t
    .split(/\s*(?:[.;\n]|,? puis |,? ensuite |,? and then |,? then |,? en dan |,? dan |,? daarna )\s*/i)
    .filter(Boolean);
  const known = analyse(project).circuits;
  /** The circuit a part of the request names: "circuit G" / "kring G", or the only one its words match. */
  const named = (part: string) => {
    const letter = /\b(?:circuit|kring)\s+([a-z])\b(?!\w)/i.exec(part)?.[1]?.toUpperCase();
    if (letter) return known.find((c) => c.code === letter)?.code;
    const hits = known.filter((c) => c.circuit?.label && namesCircuit(part, c.code, c.circuit.label));
    return hits.length === 1 ? hits[0]!.code : undefined;
  };
  for (const part of parts) {
    const s = part.toLowerCase();
    const removing = /\b(supprim\w*|retir\w*|enl[eè]v\w*|delete|remove|verwijder\w*)\b/i.test(s);
    const fewer =
      /(\d+)\s*(prises?|sockets?|points?|lampes?|lights?|stopcontact(?:en)?|lichtpunt(?:en)?)/i.exec(s);
    const target = named(part);
    if (removing && fewer && target) {
      actions.push({
        type: 'remove_points',
        code: target,
        kind: /prise|socket|stopcontact/i.test(fewer[2]!) ? 'socket' : 'light',
        count: Number(fewer[1]),
      });
      continue;
    }
    if (removing && target) {
      actions.push({ type: 'delete_circuit', code: target });
      continue;
    }
    const mainSwitch = /(interrupteur|sectionneur)\s+g[ée]n[ée]ral|main\s+switch|hoofdschakelaar/i.test(s)
      ? /(\d{2,3})\s?A\b/i.exec(part)
      : null;
    if (mainSwitch) {
      actions.push({ type: 'set_main_switch', ratingA: Number(mainSwitch[1]), poles: null });
      continue;
    }
    const under =
      /\b(sous|under|onder)\b/i.test(s) && /\b(d[ée]plac\w*|mets?|move|put|verplaats\w*|zet)\b/i.test(s);
    if (under && target) {
      const tag = /\b(Q\d+)\b/i.exec(part)?.[1];
      const ma = /(\d{2,3})\s?mA/i.exec(part)?.[1];
      if (tag || ma) {
        actions.push({
          type: 'move_circuit',
          code: target,
          rcd: tag ?? null,
          sensitivityMa: ma ? Number(ma) : null,
        });
        continue;
      }
    }
    const upd = /circuit\s+([a-z])\b(?!\w)/i.exec(part);
    if (
      upd &&
      /\b(passe|mets?|change|modifi|set|make|en|to|zet|wijzig|verander|naar|op)\b/i.test(s) &&
      !/\b(ajout|add|nouveau|new|voeg|nieuwe?)\b/i.test(s)
    ) {
      const rating = /\b([BCD])?\s?(\d{1,2})\s?A\b/i.exec(part.slice(upd.index + upd[0].length));
      const section = /(\d+(?:[.,]\d+)?)\s?mm/i.exec(part);
      const label =
        /(?:renomm\w*|rename\w*|appel\w*|call|hernoem\w*|noem\w*)\s+(?:en\s+|to\s+|as\s+|naar\s+)?[«"“]?([^"»”]+)[»"”]?/i.exec(
          part,
        );
      actions.push({
        type: 'update_circuit',
        code: upd[1]!.toUpperCase(),
        label: label ? label[1]!.trim() : null,
        ratingA: rating ? Number(rating[2]) : null,
        curve: rating?.[1] ? (rating[1].toUpperCase() as 'B' | 'C' | 'D') : null,
        cable: section ? { type: 'XVB', cores: 3, sectionMm2: Number(section[1]!.replace(',', '.')) } : null,
      });
      continue;
    }
    // Demo: the missing points go on the plan (aside, with no rooms, when there is none yet).
    if (/plan de position|position plan|situatieschema|R-DOC-03/i.test(s)) {
      actions.push({ type: 'place_missing_points', points: null });
      continue;
    }
    if (/multifilaire|multi-?line|multi-?filaire|wiring diagram|meerdraads/i.test(s)) {
      actions.push({ type: 'generate_multiline' });
      continue;
    }
    if (/(num[ée]rot|number|nummer).*(fils|wires|conducteurs|draden)/i.test(s)) {
      actions.push({ type: 'number_wires' });
      continue;
    }
    if (/(num[ée]rot|number|nummer).*(bornes|terminals|klemmen)/i.test(s)) {
      actions.push({ type: 'number_terminals' });
      continue;
    }
    if (/ren?um[ée]rot|renumber|hernummer/i.test(s)) {
      actions.push({ type: 'renumber' });
      continue;
    }
    const tpl = TEMPLATE_WORDS.find(([re]) => re.test(s));
    if (tpl && !/\d+\s*(prises?|sockets?|points?|lampes?|stopcontact(?:en)?|lichtpunt(?:en)?)/i.test(s)) {
      actions.push({ type: 'insert_template', templateId: tpl[1] });
      continue;
    }
    if (/\b(ajout\w*|add|cr[ée]e\w*|create|nouveau|new|voeg\w*|maak|nieuwe?)\b/i.test(s)) {
      const body = part.replace(
        /^.*?\b(?:ajout\w*|add|cr[ée]e\w*|create|voeg\w*|maak)\b\s*(?:(?:un|une|a|an|le|la|een|de|het)\s+)?(?:(?:nouveau|new|nieuwe?)\s+)?(?:(?:circuits?|kring(?:en)?)\s*(?:pour|for|voor|:)?\s*)?(?:toe\s+)?/i,
        '',
      );
      const circuits = parseCircuitText(body).filter((c) => c.points.length || c.ratingA);
      if (circuits.length) {
        actions.push({ type: 'add_circuits', circuits });
        continue;
      }
    }
  }
  if (!actions.length)
    return {
      reply: tr(
        L,
        'Mode démo : je comprends par exemple « ajoute un circuit cuisine 6 prises 20 A 2,5 mm² », « passe le circuit C en 20 A », « ajoute une borne de recharge », « génère le multifilaire », « numérote les fils », « numérote les bornes », « renumérote les circuits ».',
        'Demo mode: I understand e.g. “add a kitchen circuit 6 sockets 20 A 2.5 mm²”, “set circuit C to 20 A”, “add an EV charger”, “generate the multi-line diagram”, “number the wires”, “number the terminals”, “renumber the circuits”.',
        'Demomodus: ik begrijp bijvoorbeeld “voeg een kring keuken toe 6 stopcontacten 20 A 2,5 mm²”, “zet kring C op 20 A”, “voeg een laadpaal toe”, “maak het meerdraadsschema”, “nummer de draden”, “nummer de klemmen”, “hernummer de kringen”.',
      ),
      actions: [],
    };
  return {
    reply: tr(L, 'Voici ce que je propose :', 'Here is what I suggest:', 'Dit stel ik voor:'),
    actions,
  };
}
